<template>
  <Teleport to="body">
    <!-- FAB button -->
    <Transition name="quick-add-fab">
      <button
        v-if="visible"
        type="button"
        class="quick-add-fab"
        :style="fabButtonStyle"
        :aria-label="t('app.quickAddItemAria')"
        data-testid="quick-add-item-fab"
        @click="openSheet"
      >
        <span class="quick-add-fab__icon material-icons" aria-hidden="true">add</span>
        <span class="quick-add-fab__label">{{ t('app.quickAddItem') }}</span>
      </button>
    </Transition>

    <!-- Sheet overlay -->
    <Transition name="quick-add-overlay">
      <div
        v-if="sheetOpen"
        class="quick-add-overlay"
        aria-hidden="true"
      />
    </Transition>

    <!-- Bottom sheet -->
    <Transition name="quick-add-sheet">
      <div
        v-if="sheetOpen"
        ref="sheetRootRef"
        class="quick-add-sheet"
        role="dialog"
        aria-modal="true"
        :aria-label="t('app.quickAddItemAria')"
        data-testid="quick-add-sheet"
      >
        <!-- Drag handle: swipe-to-dismiss only from here (not the scrollable form — avoids accidental close). -->
        <div
          class="quick-add-sheet__handle-area"
          @touchstart.passive="onHandleTouchStart"
          @touchmove.passive="onHandleTouchMove"
          @touchend.passive="onHandleTouchEnd"
          @touchcancel.passive="onHandleTouchCancel"
        >
          <div class="quick-add-sheet__handle" />
        </div>

        <div v-if="!isDone" class="quick-add-sheet__biz-strip" role="note">
          <span class="material-icons quick-add-sheet__biz-strip-icon" aria-hidden="true">storefront</span>
          <span class="quick-add-sheet__biz-strip-text">{{ t('app.quickAddBusinessContext') }}</span>
        </div>

        <!-- Header -->
        <div class="quick-add-sheet__head">
          <p class="quick-add-sheet__eyebrow">{{ stepEyebrow }}</p>
          <!-- eslint-disable-next-line vue/no-v-html -->
          <h2 class="quick-add-sheet__title" v-html="stepTitle" />
        </div>

        <!-- Progress pips -->
        <div v-if="!isDone" class="quick-add-sheet__progress" role="progressbar" :aria-valuenow="step + 1" :aria-valuemax="TOTAL_STEPS">
          <div
            v-for="i in TOTAL_STEPS"
            :key="i"
            class="quick-add-sheet__pip"
            :class="{
              'quick-add-sheet__pip--done': i - 1 < step,
              'quick-add-sheet__pip--active': i - 1 === step,
            }"
          />
        </div>

        <!-- Body -->
        <div class="quick-add-sheet__body" ref="bodyRef">
          <!-- Step 0: Intent + (create) type -->
          <div v-if="step === 0" class="step-anim">
            <fieldset class="quick-add-sheet__intent-fieldset">
              <legend class="quick-add-sheet__sec-label quick-add-sheet__intent-legend">
                {{ t('app.quickAddIntentLabel') }}
              </legend>
              <div
                class="quick-add-sheet__intent-cards"
                role="radiogroup"
                :aria-label="t('app.quickAddIntentLabel')"
              >
                <button
                  type="button"
                  role="radio"
                  class="quick-add-sheet__intent-card"
                  :class="{ 'quick-add-sheet__intent-card--sel': form.intent === 'create' }"
                  :aria-checked="form.intent === 'create'"
                  @click="form.intent = 'create'"
                >
                  <span class="quick-add-sheet__intent-card-radio" aria-hidden="true">
                    <span class="quick-add-sheet__intent-card-radio-dot" />
                  </span>
                  <span class="quick-add-sheet__intent-card-main">
                    <span class="quick-add-sheet__intent-card-title">{{ t('app.quickAddIntentCreate') }}</span>
                    <span class="quick-add-sheet__intent-card-desc">{{ t('app.quickAddIntentCreateSub') }}</span>
                  </span>
                  <span class="quick-add-sheet__intent-card-icon material-icons" aria-hidden="true">post_add</span>
                </button>
                <button
                  type="button"
                  role="radio"
                  class="quick-add-sheet__intent-card"
                  :class="{ 'quick-add-sheet__intent-card--sel': form.intent === 'assign' }"
                  :aria-checked="form.intent === 'assign'"
                  @click="form.intent = 'assign'"
                >
                  <span class="quick-add-sheet__intent-card-radio" aria-hidden="true">
                    <span class="quick-add-sheet__intent-card-radio-dot" />
                  </span>
                  <span class="quick-add-sheet__intent-card-main">
                    <span class="quick-add-sheet__intent-card-title">{{ t('app.quickAddIntentAssign') }}</span>
                    <span class="quick-add-sheet__intent-card-desc">{{ t('app.quickAddIntentAssignSub') }}</span>
                  </span>
                  <span class="quick-add-sheet__intent-card-icon material-icons" aria-hidden="true">inventory_2</span>
                </button>
              </div>
            </fieldset>
            <template v-if="form.intent === 'create'">
              <p class="quick-add-sheet__sec-label quick-add-sheet__sec-label--after-intent">{{ t('app.quickAddWhatKind') }}</p>
              <div class="quick-add-sheet__type-grid">
                <button
                  v-for="opt in typeOptions"
                  :key="opt.id"
                  type="button"
                  class="quick-add-sheet__type-chip"
                  :class="{ 'quick-add-sheet__type-chip--sel': form.type === opt.id }"
                  @click="form.type = opt.id"
                >
                  <span class="quick-add-sheet__chip-icon material-icons" aria-hidden="true">{{ opt.icon }}</span>
                  <span class="quick-add-sheet__chip-label">{{ opt.label }}</span>
                  <span class="quick-add-sheet__chip-sub">{{ opt.sub }}</span>
                </button>
              </div>
              <div class="quick-add-sheet__type-hint my-3">{{ typeHint }}</div>
            </template>
            <p v-else class="quick-add-sheet__type-hint">{{ t('app.quickAddIntentAssignHint') }}</p>
          </div>

          <!-- Step 1: Category -->
          <div v-else-if="step === 1" class="step-anim">
            <div v-if="categoriesLoading" class="quick-add-sheet__skeleton-list">
              <div v-for="n in 3" :key="n" class="quick-add-sheet__skeleton-row" />
            </div>
            <template v-else>
              <p
                v-if="form.intent === 'assign'"
                class="quick-add-sheet__assign-cat-hint"
              >
                {{ t('app.quickAddAssignCategoryHint') }}
              </p>
              <p v-if="categoryStepError" class="quick-add-sheet__field-error" role="alert">{{ categoryStepError }}</p>
              <div v-if="categories.length === 0" class="quick-add-sheet__empty-cat">
                <span class="material-icons quick-add-sheet__empty-cat-icon" aria-hidden="true">folder_open</span>
                <p class="quick-add-sheet__empty-cat-title">{{ t('app.quickAddNoCats') }}</p>
                <p class="quick-add-sheet__empty-cat-sub">
                  {{ form.intent === 'assign' ? t('app.quickAddAssignNoCategories') : t('app.quickAddNoCatsSub') }}
                </p>
              </div>
              <div v-else class="quick-add-sheet__cat-list">
                <button
                  v-for="cat in categories"
                  :key="cat.uuid"
                  type="button"
                  class="quick-add-sheet__cat-card"
                  :class="{ 'quick-add-sheet__cat-card--sel': form.categoryUuid === cat.uuid }"
                  @click="selectCategory(cat.uuid)"
                >
                  <div class="quick-add-sheet__cat-info">
                    <span class="quick-add-sheet__cat-name">{{ cat.name }}</span>
                    <span class="quick-add-sheet__cat-meta">{{ cat.itemCount }} {{ cat.itemCount === 1 ? t('app.quickAddItem1') : t('app.quickAddItemN') }}</span>
                  </div>
                  <div class="quick-add-sheet__cat-check" :class="{ 'quick-add-sheet__cat-check--on': form.categoryUuid === cat.uuid }">
                    <span v-if="form.categoryUuid === cat.uuid" class="material-icons" style="font-size:13px;" aria-hidden="true">check</span>
                  </div>
                </button>
              </div>

              <div v-if="categories.length > 0" class="quick-add-sheet__or-divider">
                <span class="quick-add-sheet__or-line" /><span class="quick-add-sheet__or-text">{{ t('app.quickAddOrCreate') }}</span><span class="quick-add-sheet__or-line" />
              </div>
            </template>

            <div class="quick-add-sheet__create-box">
              <p class="quick-add-sheet__create-label">{{ categories.length === 0 ? t('app.quickAddFirstCatName') : t('app.quickAddNewCat') }}</p>
              <div class="quick-add-sheet__create-row">
                <input
                  v-model="form.newCategoryName"
                  type="text"
                  class="quick-add-sheet__create-input"
                  :placeholder="t('app.quickAddCatPlaceholder')"
                  autocomplete="off"
                  maxlength="255"
                  @keydown.enter.prevent="onCreateCategoryEnter"
                  @input="onNewCatNameInput"
                />
                <button
                  type="button"
                  class="quick-add-sheet__create-btn"
                  :disabled="!form.newCategoryName.trim()"
                  @click="confirmNewCategory"
                >
                  {{ t('app.quickAddCreate') }}
                </button>
              </div>
              <p v-if="form.newCategoryPending" class="quick-add-sheet__cat-pending">
                <span class="material-icons" style="font-size:14px;vertical-align:-2px;">check_circle</span>
                {{ t('app.quickAddCatWillCreate', { name: form.newCategoryPending }) }}
              </p>
            </div>
          </div>

          <!-- Step 2a: Create — details -->
          <div v-else-if="step === 2 && form.intent === 'create'" class="step-anim">
            <!-- Name (all types) -->
            <div class="quick-add-sheet__field">
              <label class="quick-add-sheet__label" for="qai-name">{{ t('app.quickAddNameLabel') }}</label>
              <input
                id="qai-name"
                v-model="form.name"
                type="text"
                class="quick-add-sheet__input"
                :class="{ 'quick-add-sheet__input--error': fieldErrors.name }"
                :placeholder="t('app.quickAddNamePlaceholder')"
                autocomplete="off"
                maxlength="255"
              />
              <p v-if="fieldErrors.name" class="quick-add-sheet__field-error">{{ fieldErrors.name }}</p>
            </div>

            <!-- Simple: price -->
            <div v-if="form.type === 'simple'" class="quick-add-sheet__two-col">
              <div class="quick-add-sheet__field">
                <label class="quick-add-sheet__label" for="qai-price">{{ t('app.quickAddPriceOptional') }}</label>
                <input
                  id="qai-price"
                  v-model="form.price"
                  type="number"
                  min="0"
                  step="0.01"
                  inputmode="decimal"
                  class="quick-add-sheet__input"
                  :placeholder="t('app.quickAddPricePlaceholder')"
                />
              </div>
            </div>

            <!-- Combo: combo price -->
            <div v-if="form.type === 'combo'" class="quick-add-sheet__field">
              <label class="quick-add-sheet__label" for="qai-combo-price">{{ t('app.quickAddComboPriceOptional') }}</label>
              <input
                id="qai-combo-price"
                v-model="form.comboPrice"
                type="number"
                min="0"
                step="0.01"
                inputmode="decimal"
                class="quick-add-sheet__input"
                :placeholder="t('app.quickAddPricePlaceholder')"
              />
            </div>

            <!-- Description (all types) -->
            <div class="quick-add-sheet__field">
              <label class="quick-add-sheet__label" for="qai-desc">{{ t('app.quickAddDescOptional') }}</label>
              <textarea
                id="qai-desc"
                v-model="form.description"
                rows="2"
                class="quick-add-sheet__textarea"
                :placeholder="t('app.quickAddDescPlaceholder')"
                maxlength="5000"
              />
            </div>

            <!-- Photo + modifiers (catalog item): simple & with_variants -->
            <template v-if="form.type === 'simple' || form.type === 'with_variants'">
              <div class="quick-add-sheet__photo-section">
                <p class="quick-add-sheet__sec-label">{{ t('app.quickAddPhotoSection') }}</p>
                <p class="quick-add-sheet__photo-hint">{{ t('app.quickAddPhotoHintCrop') }}</p>
                <div class="quick-add-sheet__photo-row">
                  <div v-if="itemImagePreviewUrl" class="quick-add-sheet__photo-preview-wrap">
                    <img
                      :src="itemImagePreviewUrl"
                      :alt="t('app.quickAddPhotoPreviewAlt')"
                      class="quick-add-sheet__photo-preview"
                    />
                  </div>
                  <div v-else class="quick-add-sheet__photo-placeholder" aria-hidden="true">
                    <span class="material-icons quick-add-sheet__photo-placeholder-icon">add_photo_alternate</span>
                  </div>
                  <div class="quick-add-sheet__photo-actions">
                    <input
                      ref="itemImageInputRef"
                      type="file"
                      class="quick-add-sheet__sr-only"
                      :accept="ACCEPT_IMAGE"
                      tabindex="-1"
                      @change="onItemImageChange"
                    />
                    <button
                      type="button"
                      class="quick-add-sheet__photo-upload-btn"
                      @click="itemImageInputRef?.click()"
                    >
                      {{ itemImageFile ? t('app.quickAddPhotoReplace') : t('app.quickAddPhotoUpload') }}
                    </button>
                    <button
                      v-if="itemImageFile"
                      type="button"
                      class="quick-add-sheet__photo-remove-btn"
                      @click="clearItemImage"
                    >
                      {{ t('app.quickAddPhotoRemove') }}
                    </button>
                  </div>
                </div>
              </div>

              <div class="quick-add-sheet__mod-section">
                <div class="quick-add-sheet__mod-head">
                  <p class="quick-add-sheet__sec-label" style="margin-bottom: 4px;">{{ t('app.menuItemModifiersSectionTitle') }}</p>
                  <button type="button" class="quick-add-sheet__add-group-chip" @click="addQuickModifierGroup">
                    <span class="material-icons" style="font-size:16px;" aria-hidden="true">add</span>
                    {{ t('app.addModifierGroup') }}
                  </button>
                </div>
                <p class="quick-add-sheet__type-hint quick-add-sheet__mod-hint">{{ t('app.quickAddModifiersHint') }}</p>

                <div
                  v-for="(grp, gIdx) in form.modifier_groups"
                  :key="gIdx"
                  class="quick-add-sheet__mod-group"
                >
                  <div class="quick-add-sheet__mod-group-toolbar">
                    <span class="quick-add-sheet__mod-group-num">{{ gIdx + 1 }}</span>
                    <button
                      type="button"
                      class="quick-add-sheet__rm-btn"
                      :aria-label="t('app.removeModifierGroupAria')"
                      @click="removeQuickModifierGroup(gIdx)"
                    >
                      <span class="material-icons" style="font-size:18px;" aria-hidden="true">close</span>
                    </button>
                  </div>
                  <label class="quick-add-sheet__label" :for="`qai-mg-name-${gIdx}`">{{ t('app.modifierGroupNameLabel') }}</label>
                  <input
                    :id="`qai-mg-name-${gIdx}`"
                    v-model="grp.name"
                    type="text"
                    class="quick-add-sheet__input"
                    :placeholder="t('app.modifierGroupNameLabel')"
                    autocomplete="off"
                    maxlength="255"
                  />
                  <div
                    class="quick-add-sheet__mod-radio-row"
                    role="radiogroup"
                    :aria-label="t('app.modifierGroupSelectionTypeLabel')"
                  >
                    <span class="quick-add-sheet__mod-radio-label">{{ t('app.modifierGroupSelectionTypeLabel') }}</span>
                    <label class="quick-add-sheet__mod-radio">
                      <input v-model="grp.selection_type" type="radio" value="single" />
                      <span>{{ t('app.modifierSelectionSingle') }}</span>
                    </label>
                    <label class="quick-add-sheet__mod-radio">
                      <input v-model="grp.selection_type" type="radio" value="multiple" />
                      <span>{{ t('app.modifierSelectionMultiple') }}</span>
                    </label>
                  </div>
                  <p class="quick-add-sheet__label" style="margin-top: 10px;">{{ t('app.modifierGroupOptionsHeading') }}</p>
                  <button
                    type="button"
                    class="quick-add-sheet__add-opt-chip"
                    @click="addQuickModifierOption(gIdx)"
                  >
                    <span class="material-icons" style="font-size:15px;" aria-hidden="true">add</span>
                    {{ t('app.addModifierOption') }}
                  </button>
                  <div
                    v-for="(mod, oIdx) in grp.options"
                    :key="oIdx"
                    class="quick-add-sheet__mod-option"
                  >
                    <div class="quick-add-sheet__mod-option-head">
                      <span class="quick-add-sheet__mod-option-num">{{ oIdx + 1 }}</span>
                      <button
                        v-if="grp.options.length > 1"
                        type="button"
                        class="quick-add-sheet__rm-btn"
                        :aria-label="t('app.removeModifierAria')"
                        @click="removeQuickModifierOption(gIdx, oIdx)"
                      >
                        <span class="material-icons" style="font-size:16px;" aria-hidden="true">close</span>
                      </button>
                    </div>
                    <label class="quick-add-sheet__label" :for="`qai-mo-name-${gIdx}-${oIdx}`">{{ t('app.modifierOptionNameLabel') }}</label>
                    <input
                      :id="`qai-mo-name-${gIdx}-${oIdx}`"
                      v-model="mod.translations[defaultLocale].name"
                      type="text"
                      class="quick-add-sheet__input"
                      autocomplete="off"
                      maxlength="255"
                    />
                    <label class="quick-add-sheet__label" :for="`qai-mo-pd-${gIdx}-${oIdx}`">{{ t('app.modifierPriceDeltaLabel') }}</label>
                    <input
                      :id="`qai-mo-pd-${gIdx}-${oIdx}`"
                      v-model.number="mod.price_delta"
                      type="number"
                      min="0"
                      step="0.01"
                      inputmode="decimal"
                      class="quick-add-sheet__input"
                    />
                  </div>
                </div>
              </div>
            </template>

            <!-- Variants (with_variants): option groups + price per combination (same as catalog) -->
            <template v-if="form.type === 'with_variants'">
              <div class="quick-add-sheet__opt-groups-head">
                <p class="quick-add-sheet__sec-label" style="margin-bottom:8px;">{{ t('app.optionGroupsTitle') }}</p>
                <button type="button" class="quick-add-sheet__add-group-chip" @click="addOptionGroup">
                  <span class="material-icons" style="font-size:16px;" aria-hidden="true">add</span>
                  {{ t('app.addOptionGroup') }}
                </button>
              </div>
              <p v-if="fieldErrors.variant_option_groups" class="quick-add-sheet__field-error">{{ fieldErrors.variant_option_groups }}</p>
              <div
                v-for="(grp, gIdx) in form.variant_option_groups"
                :key="gIdx"
                class="quick-add-sheet__opt-group"
              >
                <div class="quick-add-sheet__opt-group-toolbar">
                  <span class="quick-add-sheet__opt-group-num">{{ gIdx + 1 }}</span>
                  <button
                    v-if="form.variant_option_groups.length > 1"
                    type="button"
                    class="quick-add-sheet__rm-btn"
                    :aria-label="t('app.removeOptionGroupAria')"
                    @click="removeOptionGroup(gIdx)"
                  >
                    <span class="material-icons" style="font-size:18px;" aria-hidden="true">close</span>
                  </button>
                </div>
                <label class="quick-add-sheet__label" :for="`qai-og-name-${gIdx}`">{{ t('app.catalogGroupNameLabelWithHint', { example: gIdx === 0 ? t('app.exampleSize') : t('app.exampleType') }) }}</label>
                <input
                  :id="`qai-og-name-${gIdx}`"
                  v-model="grp.name"
                  type="text"
                  class="quick-add-sheet__input"
                  :class="{ 'quick-add-sheet__input--error': fieldErrors[`variant_option_groups.${gIdx}.name`] }"
                  :placeholder="t('app.catalogGroupNamePlaceholder')"
                  autocomplete="off"
                  maxlength="255"
                  @input="updateCartesianSkus"
                />
                <label class="quick-add-sheet__label" :for="`qai-og-values-${gIdx}`">{{ t('app.optionValuesOnePerLine') }}</label>
                <textarea
                  :id="`qai-og-values-${gIdx}`"
                  v-model="grp.valuesText"
                  rows="2"
                  class="quick-add-sheet__textarea"
                  :placeholder="t('app.variantSizesPlaceholder')"
                  @input="syncGroupValues(gIdx)"
                />
              </div>

              <template v-if="form.variant_skus.length > 0">
                <p class="quick-add-sheet__sec-label" style="margin-top:14px;margin-bottom:8px;">{{ t('app.variantPricesTitle') }}</p>
                <p v-if="fieldErrors.variant_skus" class="quick-add-sheet__field-error">{{ fieldErrors.variant_skus }}</p>
                <div
                  v-for="(row, rIdx) in form.variant_skus"
                  :key="rIdx"
                  class="quick-add-sheet__variant-price-row"
                >
                  <span class="quick-add-sheet__variant-price-label">{{ row.label }}</span>
                  <input
                    v-model="row.price"
                    type="number"
                    min="0"
                    step="0.01"
                    inputmode="decimal"
                    class="quick-add-sheet__input quick-add-sheet__variant-price-input"
                    :placeholder="t('app.placeholderVariantPrice')"
                    :aria-label="t('app.ariaPriceForVariant', { label: row.label || t('app.genericVariant') })"
                  />
                </div>
              </template>
            </template>

            <!-- Combo items -->
            <template v-if="form.type === 'combo'">
              <p class="quick-add-sheet__sec-label" style="margin-bottom:8px;">{{ t('app.quickAddComboItems') }}</p>
              <div v-if="itemsLoading" class="quick-add-sheet__skeleton-list">
                <div v-for="n in 2" :key="n" class="quick-add-sheet__skeleton-row" />
              </div>
              <template v-else>
                <div v-if="form.comboEntries.length > 0" class="quick-add-sheet__combo-list">
                  <div
                    v-for="(entry, idx) in form.comboEntries"
                    :key="entry.pickerKey"
                    class="quick-add-sheet__combo-row"
                  >
                    <span class="quick-add-sheet__combo-name">{{ entry.name }}</span>
                    <div class="quick-add-sheet__qty-wrap">
                      <button type="button" class="quick-add-sheet__qty-btn" @click="changeQty(idx, -1)">
                        <span class="material-icons" style="font-size:16px;" aria-hidden="true">remove</span>
                      </button>
                      <span class="quick-add-sheet__qty-val">{{ entry.quantity }}</span>
                      <button type="button" class="quick-add-sheet__qty-btn" @click="changeQty(idx, 1)">
                        <span class="material-icons" style="font-size:16px;" aria-hidden="true">add</span>
                      </button>
                      <button type="button" class="quick-add-sheet__rm-btn" :aria-label="t('app.quickAddRemoveComboItem')" @click="removeComboEntry(idx)">
                        <span class="material-icons" style="font-size:16px;" aria-hidden="true">close</span>
                      </button>
                    </div>
                  </div>
                </div>
                <p v-if="fieldErrors.combo_entries" class="quick-add-sheet__field-error">{{ fieldErrors.combo_entries }}</p>

                <!-- Add from existing items -->
                <div v-if="availableComboPickerRows.length > 0" class="quick-add-sheet__combo-add-row">
                  <select v-model="comboPickerKey" class="quick-add-sheet__select">
                    <option value="">{{ t('app.quickAddPickItem') }}</option>
                    <option v-for="row in availableComboPickerRows" :key="row.pickerKey" :value="row.pickerKey">
                      {{ row.name }}{{ row.price != null ? ` — ${formatPrice(row.price)}` : '' }}
                    </option>
                  </select>
                  <button
                    type="button"
                    class="quick-add-sheet__create-btn"
                    :disabled="!comboPickerKey"
                    @click="addComboEntry"
                  >
                    {{ t('app.quickAddAdd') }}
                  </button>
                </div>
                <p v-else-if="!itemsLoading && comboPickerRows.length === 0" class="quick-add-sheet__empty-hint">
                  {{ t('app.quickAddNoItemsForCombo') }}
                </p>
              </template>
            </template>
          </div>

          <!-- Step 2b: Assign — pick catalog item -->
          <div v-else-if="step === 2 && form.intent === 'assign'" class="step-anim">
            <p class="quick-add-sheet__sec-label">{{ t('app.quickAddPickCatalogItem') }}</p>
            <label class="quick-add-sheet__sr-only" for="qai-assign-search">{{ t('app.quickAddAssignSearchPlaceholder') }}</label>
            <input
              id="qai-assign-search"
              v-model="assignSearchQuery"
              type="search"
              class="quick-add-sheet__input"
              :placeholder="t('app.quickAddAssignSearchPlaceholder')"
              autocomplete="off"
            />
            <div v-if="assignCatalogLoading" class="quick-add-sheet__skeleton-list" style="margin-top: 12px;">
              <div v-for="n in 4" :key="n" class="quick-add-sheet__skeleton-row" />
            </div>
            <p v-else-if="assignCatalogError" class="quick-add-sheet__field-error" role="alert">{{ assignCatalogError }}</p>
            <p v-else-if="!assignCatalogLoading && assignFilteredRows.length === 0" class="quick-add-sheet__empty-hint">
              {{ t('app.quickAddAssignEmpty') }}
            </p>
            <ul v-else class="quick-add-assign-list" role="listbox" :aria-label="t('app.quickAddPickCatalogItem')">
              <li
                v-for="row in assignFilteredRows"
                :key="row.uuid"
                role="presentation"
                class="quick-add-assign-li"
              >
                <button
                  type="button"
                  role="option"
                  class="quick-add-assign-row"
                  :class="{ 'quick-add-assign-row--sel': selectedAssignRow?.uuid === row.uuid }"
                  :aria-selected="selectedAssignRow?.uuid === row.uuid"
                  @click="selectedAssignRow = row"
                >
                  <span class="quick-add-assign-row__name">{{ assignRowDisplayName(row) }}</span>
                  <span class="quick-add-assign-row__end">
                    <span
                      v-if="row.price != null && row.price !== ''"
                      class="quick-add-assign-row__price"
                    >{{ formatPrice(Number(row.price) || 0) }}</span>
                    <span
                      class="quick-add-assign-row__check"
                      :class="{ 'quick-add-assign-row__check--on': selectedAssignRow?.uuid === row.uuid }"
                      aria-hidden="true"
                    >
                      <span
                        v-if="selectedAssignRow?.uuid === row.uuid"
                        class="material-icons quick-add-assign-row__check-icon"
                      >check</span>
                    </span>
                  </span>
                </button>
              </li>
            </ul>
          </div>

          <!-- Step 3: Review & save -->
          <div v-else-if="step === 3 && !isDone" class="step-anim">
            <div class="quick-add-sheet__review-card">
              <p class="quick-add-sheet__review-head">{{ t('app.quickAddReviewItem') }}</p>
              <template v-if="form.intent === 'assign'">
                <div class="quick-add-sheet__review-row">
                  <span class="quick-add-sheet__rk">{{ t('app.quickAddReviewName') }}</span>
                  <span class="quick-add-sheet__rv">{{ selectedAssignRow ? assignRowDisplayName(selectedAssignRow) : '—' }}</span>
                </div>
                <div class="quick-add-sheet__review-row">
                  <span class="quick-add-sheet__rk">{{ t('app.quickAddReviewType') }}</span>
                  <span class="quick-add-sheet__rv">{{ t('app.quickAddReviewAssignFromCatalog') }}</span>
                </div>
                <div
                  v-if="selectedAssignRow && selectedAssignRow.price != null && selectedAssignRow.price !== ''"
                  class="quick-add-sheet__review-row"
                >
                  <span class="quick-add-sheet__rk">{{ t('app.quickAddReviewPrice') }}</span>
                  <span class="quick-add-sheet__rv">{{ formatPrice(Number(selectedAssignRow.price) || 0) }}</span>
                </div>
              </template>
              <template v-else>
                <div class="quick-add-sheet__review-row">
                  <span class="quick-add-sheet__rk">{{ t('app.quickAddReviewName') }}</span>
                  <span class="quick-add-sheet__rv">{{ form.name || '—' }}</span>
                </div>
                <div class="quick-add-sheet__review-row">
                  <span class="quick-add-sheet__rk">{{ t('app.quickAddReviewType') }}</span>
                  <span class="quick-add-sheet__rv">{{ typeLabel }}</span>
                </div>
                <div class="quick-add-sheet__review-row">
                  <span class="quick-add-sheet__rk">{{ t('app.quickAddReviewPrice') }}</span>
                  <span class="quick-add-sheet__rv">{{ reviewPriceDisplay }}</span>
                </div>
                <div
                  v-if="form.type === 'simple' || form.type === 'with_variants'"
                  class="quick-add-sheet__review-row"
                >
                  <span class="quick-add-sheet__rk">{{ t('app.quickAddReviewPhoto') }}</span>
                  <span class="quick-add-sheet__rv">{{ itemImageFile ? t('app.quickAddReviewPhotoYes') : t('app.quickAddReviewPhotoNone') }}</span>
                </div>
                <div
                  v-if="form.type === 'simple' || form.type === 'with_variants'"
                  class="quick-add-sheet__review-row"
                >
                  <span class="quick-add-sheet__rk">{{ t('app.quickAddReviewModifiers') }}</span>
                  <span class="quick-add-sheet__rv">{{ reviewModifiersDisplay }}</span>
                </div>
                <div v-if="form.type === 'combo' && form.comboEntries.length > 0" class="quick-add-sheet__review-row">
                  <span class="quick-add-sheet__rk">{{ t('app.quickAddReviewIncludes') }}</span>
                  <span class="quick-add-sheet__rv">{{ form.comboEntries.map(e => `${e.quantity}× ${e.name}`).join(', ') }}</span>
                </div>
              </template>
            </div>
            <div class="quick-add-sheet__review-card">
              <p class="quick-add-sheet__review-head">{{ t('app.quickAddReviewCategory') }}</p>
              <div class="quick-add-sheet__review-row">
                <span class="quick-add-sheet__rk">{{ t('app.quickAddReviewCategoryLabel') }}</span>
                <span class="quick-add-sheet__rv">{{ reviewCategoryDisplay }}</span>
              </div>
            </div>
            <div class="quick-add-sheet__toggles">
              <div class="quick-add-sheet__toggle-row">
                <div>
                  <p class="quick-add-sheet__toggle-title">{{ t('app.quickAddPublishNow') }}</p>
                  <p class="quick-add-sheet__toggle-sub">{{ t('app.quickAddPublishNowSub') }}</p>
                </div>
                <button
                  type="button"
                  class="quick-add-sheet__tog"
                  :class="{ 'quick-add-sheet__tog--on': form.isActive }"
                  :aria-pressed="form.isActive"
                  @click="form.isActive = !form.isActive"
                >
                  <span class="quick-add-sheet__tog-knob" />
                </button>
              </div>
              <div class="quick-add-sheet__toggle-row">
                <div>
                  <p class="quick-add-sheet__toggle-title">{{ t('app.quickAddAddAnother') }}</p>
                  <p class="quick-add-sheet__toggle-sub">{{ t('app.quickAddAddAnotherSub') }}</p>
                </div>
                <button
                  type="button"
                  class="quick-add-sheet__tog"
                  :class="{ 'quick-add-sheet__tog--on': form.addAnother }"
                  :aria-pressed="form.addAnother"
                  @click="form.addAnother = !form.addAnother"
                >
                  <span class="quick-add-sheet__tog-knob" />
                </button>
              </div>
            </div>
            <p v-if="saveError" class="quick-add-sheet__field-error" role="alert">{{ saveError }}</p>
          </div>

          <!-- Done / success -->
          <div v-else-if="isDone" class="step-anim quick-add-sheet__success">
            <div class="quick-add-sheet__check-ring">
              <span class="material-icons" style="font-size:26px;color:currentColor;" aria-hidden="true">check</span>
            </div>
            <h3 class="quick-add-sheet__success-title">{{ t('app.quickAddSaved') }}</h3>
            <p class="quick-add-sheet__success-sub">
              {{ t('app.quickAddSavedSub', { name: lastSavedName, category: lastSavedCategory }) }}
            </p>
            <p v-if="imageUploadWarn" class="quick-add-sheet__success-warn" role="status">{{ imageUploadWarn }}</p>
            <div class="quick-add-sheet__success-actions">
              <button type="button" class="quick-add-sheet__btn-primary" @click="onAddAnother">
                {{ t('app.quickAddAnotherItem') }}
              </button>
              <button type="button" class="quick-add-sheet__btn-secondary" @click="closeSheet">
                {{ t('app.quickAddDone') }}
              </button>
            </div>
          </div>
        </div>

        <!-- Footer -->
        <div v-if="!isDone" class="quick-add-sheet__foot">
          <button
            type="button"
            class="quick-add-sheet__btn-back"
            :disabled="step === 0 || categoryCommitting"
            @click="goBack"
          >
            {{ t('app.quickAddBack') }}
          </button>
          <div class="quick-add-sheet__foot-right">
            <button
              type="button"
              class="quick-add-sheet__btn-foot-cancel"
              :disabled="saving || categoryCommitting"
              @click="closeSheet"
            >
              {{ t('app.cancel') }}
            </button>
            <button
              type="button"
              class="quick-add-sheet__btn-next"
            :disabled="!canGoNext || saving || categoryCommitting"
            @click="goNext"
            >
              <span
              v-if="saving || categoryCommitting"
              class="material-icons"
              style="font-size:16px;animation:spin 1s linear infinite;"
              aria-hidden="true"
            >sync</span>
              {{ saving || categoryCommitting ? t('app.saving') : nextLabel }}
            </button>
          </div>
        </div>
      </div>
    </Transition>

    <ImageCropModal
      v-if="cropImageUrl"
      :open="!!cropImageUrl"
      :image-url="cropImageUrl"
      export-format="jpeg"
      :file-name="cropFileName"
      stack-on-top
      data-testid="quick-add-item-crop-modal"
      @confirm="onQuickAddCropConfirm"
      @cancel="onQuickAddCropCancel"
    />
  </Teleport>
</template>

<script setup>
import { ref, computed, watch, nextTick, onBeforeUnmount } from 'vue'
import { useI18n } from 'vue-i18n'
import ImageCropModal from '@/components/ui/ImageCropModal.vue'
import MenuItem from '@/models/MenuItem.js'
import { restaurantService, menuItemService, normalizeApiError } from '@/services'
import { prepareImageFile, compressImageToMaxBytes, ACCEPT_IMAGE, MAX_IMAGE_BYTES } from '@/utils/imageUpload'
import { useToastStore } from '@/stores/toast'

const props = defineProps({
  restaurant: { type: Object, default: null },
  showFab: { type: Boolean, default: true },
})

const emit = defineEmits(['item-added'])

const { t, locale: i18nLocale } = useI18n()
const toastStore = useToastStore()

const TOTAL_STEPS = 4

const ALLOWED_ASSIGN_CATALOG_TYPES = ['simple', 'combo', 'with_variants']

const DEFAULT_FAB_ACCENT = '#e85a2b'

/** Public site primary (snake or camel); used for FAB to match business branding. */
const fabAccentColor = computed(() => {
  const r = props.restaurant
  let c = r?.primary_color ?? r?.primaryColor ?? ''
  if (typeof c !== 'string') return DEFAULT_FAB_ACCENT
  c = c.trim()
  if (!c) return DEFAULT_FAB_ACCENT
  if (c.startsWith('#') || c.startsWith('rgb') || c.startsWith('hsl')) return c
  if (/^[0-9a-fA-F]{3,8}$/.test(c)) return `#${c}`
  return DEFAULT_FAB_ACCENT
})

const fabButtonStyle = computed(() => ({
  '--quick-add-fab-accent': fabAccentColor.value,
}))

// ── Visibility ────────────────────────────────────────────────────────────────
const visible = computed(() => {
  if (!props.showFab) return false
  return !!props.restaurant?.viewer?.is_owner
})

// ── Sheet state ───────────────────────────────────────────────────────────────
const sheetOpen = ref(false)
const step = ref(0)
const isDone = ref(false)
const saving = ref(false)
const saveError = ref('')
const bodyRef = ref(null)
const sheetRootRef = ref(null)

const lastSavedName = ref('')
const lastSavedCategory = ref('')
const imageUploadWarn = ref('')

const itemImageInputRef = ref(null)
const itemImageFile = ref(null)
const itemImagePreviewUrl = ref(null)

/** Staging URL for ImageCropModal after file pick (catalog item photo). */
const cropImageUrl = ref('')
const cropFileName = ref('')

/** Catalog / menu copy locale for modifier option names (restaurant default). */
const defaultLocale = computed(() => props.restaurant?.default_locale ?? 'en')

// ── Form state ────────────────────────────────────────────────────────────────
function freshForm() {
  return {
    intent: 'create',
    type: 'simple',
    name: '',
    description: '',
    price: '',
    comboPrice: '',
    categoryUuid: null,
    newCategoryName: '',
    newCategoryPending: '',
    isActive: true,
    addAnother: false,
    variant_option_groups: [{ name: '', values: [], valuesText: '' }],
    variant_skus: [],
    comboEntries: [],
    modifier_groups: [],
  }
}
const form = ref(freshForm())
const fieldErrors = ref({})

// ── Data loading ──────────────────────────────────────────────────────────────
const categories = ref([])
const categoriesLoading = ref(false)
const categoriesLoaded = ref(false)
/** First menu on the restaurant — required for createCategory when assigning with a new category name */
const primaryMenuUuid = ref(null)
const categoryCommitting = ref(false)
const categoryStepError = ref('')

/** Rows for combo picker: one per simple item or per variant SKU */
const comboPickerRows = ref([])
const itemsLoading = ref(false)
const itemsLoaded = ref(false)
const comboPickerKey = ref('')

/** Assign-from-catalog flow (step 2b) */
const assignSearchQuery = ref('')
const assignCatalogLoading = ref(false)
const assignCatalogError = ref('')
const assignCatalogRows = ref([])
const restaurantMenuItemsForAssign = ref([])
const selectedAssignRow = ref(null)

// ── Options ───────────────────────────────────────────────────────────────────
const typeOptions = computed(() => [
  { id: 'simple',        icon: 'radio_button_unchecked', label: t('app.itemTypeSimple'),        sub: t('app.quickAddTypeSimpleSub') },
  { id: 'with_variants', icon: 'tune',                   label: t('app.itemTypeWithVariants'),  sub: t('app.quickAddTypeVariantsSub') },
  { id: 'combo',         icon: 'layers',                 label: t('app.itemTypeCombo'),          sub: t('app.quickAddTypeCombSub') },
])

const typeHint = computed(() => {
  const hints = {
    simple:        t('app.quickAddTypeSimpleHint'),
    with_variants: t('app.quickAddTypeVariantsHint'),
    combo:         t('app.quickAddTypeComboHint'),
  }
  return hints[form.value.type] ?? ''
})

const typeLabel = computed(() => typeOptions.value.find(o => o.id === form.value.type)?.label ?? '')

// ── Step meta ─────────────────────────────────────────────────────────────────
const stepEyebrow = computed(() => {
  if (isDone.value) return ''
  return t('app.quickAddStepOf', { step: step.value + 1, total: TOTAL_STEPS })
})

const stepTitle = computed(() => {
  if (isDone.value) return ''
  if (step.value === 0) return t('app.quickAddStep0TitleIntent')
  if (step.value === 2 && form.value.intent === 'assign') return t('app.quickAddStep2TitleAssign')
  const titles = [
    '',
    t('app.quickAddStep1Title'),
    t('app.quickAddStep2Title'),
    t('app.quickAddStep3Title'),
  ]
  return titles[step.value] ?? ''
})

const nextLabel = computed(() => {
  if (step.value === TOTAL_STEPS - 1) return t('app.quickAddSaveItem')
  if (step.value === TOTAL_STEPS - 2) return t('app.quickAddReview')
  return t('app.quickAddContinue')
})

// ── Computed for review ───────────────────────────────────────────────────────
const reviewPriceDisplay = computed(() => {
  if (form.value.type === 'with_variants') {
    const lines = form.value.variant_skus
      .filter(s => s.label && s.price !== '' && s.price != null && !Number.isNaN(Number(s.price)))
      .map(s => `${s.label} — ${formatPrice(parseFloat(s.price) || 0)}`)
    return lines.join(', ') || '—'
  }
  if (form.value.type === 'combo' && form.value.comboPrice !== '') {
    return formatPrice(parseFloat(form.value.comboPrice) || 0)
  }
  if (form.value.price !== '') return formatPrice(parseFloat(form.value.price) || 0)
  return t('app.quickAddNoPrice')
})

const reviewCategoryDisplay = computed(() => {
  if (form.value.newCategoryPending) return `${form.value.newCategoryPending} (${t('app.quickAddNew')})`
  if (form.value.categoryUuid) {
    return categories.value.find(c => c.uuid === form.value.categoryUuid)?.name ?? '—'
  }
  return t('app.quickAddNoCategory')
})

const reviewModifiersDisplay = computed(() => {
  const loc = defaultLocale.value
  const parts = []
  for (const grp of form.value.modifier_groups ?? []) {
    const gn = (grp.name ?? '').trim()
    const opts = (grp.options ?? [])
      .map(o => (o.translations?.[loc]?.name ?? '').trim())
      .filter(Boolean)
    if (!gn || !opts.length) continue
    const st = grp.selection_type === 'multiple' ? t('app.modifierSelectionMultiple') : t('app.modifierSelectionSingle')
    parts.push(`${gn} (${st}): ${opts.join(', ')}`)
  }
  return parts.length ? parts.join(' · ') : t('app.quickAddReviewModifiersNone')
})

function modifierOptionName(grp, oIdx) {
  const loc = defaultLocale.value
  return (grp.options?.[oIdx]?.translations?.[loc]?.name ?? '').trim()
}

/** True when every started modifier group is complete (or empty groups are ignored). */
function modifierGroupsStepValid() {
  const loc = defaultLocale.value
  for (const grp of form.value.modifier_groups ?? []) {
    const gn = (grp.name ?? '').trim()
    const opts = grp.options ?? []
    const namedOpts = opts.filter((_, oi) => modifierOptionName(grp, oi))
    const isUnused = !gn && namedOpts.length === 0
    if (isUnused) continue
    if (gn && namedOpts.length === 0) return false
    if (!gn && namedOpts.length > 0) return false
    for (let oi = 0; oi < opts.length; oi++) {
      const on = modifierOptionName(grp, oi)
      if (!on) return false
      const m = opts[oi]
      const pd = m.price_delta === '' || m.price_delta == null ? 0 : Number(m.price_delta)
      if (Number.isNaN(pd) || pd < 0) return false
    }
  }
  return true
}

function newEmptyQuickModifierOption(loc) {
  return {
    price_delta: 0,
    translations: { [loc]: { name: '' } },
  }
}

function addQuickModifierGroup() {
  const loc = defaultLocale.value
  if (!Array.isArray(form.value.modifier_groups)) form.value.modifier_groups = []
  form.value.modifier_groups.push({
    name: '',
    selection_type: 'single',
    options: [newEmptyQuickModifierOption(loc)],
  })
}

function removeQuickModifierGroup(gIdx) {
  form.value.modifier_groups.splice(gIdx, 1)
}

function addQuickModifierOption(gIdx) {
  const grp = form.value.modifier_groups[gIdx]
  if (!grp) return
  const loc = defaultLocale.value
  if (!Array.isArray(grp.options)) grp.options = []
  grp.options.push(newEmptyQuickModifierOption(loc))
}

function removeQuickModifierOption(gIdx, oIdx) {
  const grp = form.value.modifier_groups[gIdx]
  if (!grp?.options || grp.options.length < 2) return
  grp.options.splice(oIdx, 1)
}

function buildQuickAddModifierGroupsPayload() {
  const loc = defaultLocale.value
  return (form.value.modifier_groups ?? [])
    .map((g, gi) => ({
      name: (g.name ?? '').trim(),
      selection_type: g.selection_type === 'multiple' ? 'multiple' : 'single',
      sort_order: gi,
      options: (g.options ?? [])
        .map((m, oi) => ({
          sort_order: oi,
          price_delta: m.price_delta != null && !Number.isNaN(Number(m.price_delta)) ? Number(m.price_delta) : 0,
          translations: { [loc]: { name: (m.translations?.[loc]?.name ?? '').trim() } },
        }))
        .filter(o => o.translations[loc].name !== ''),
    }))
    .filter(g => g.name !== '' && g.options.length > 0)
}

function clearCropState() {
  if (cropImageUrl.value) URL.revokeObjectURL(cropImageUrl.value)
  cropImageUrl.value = ''
  cropFileName.value = ''
}

async function onItemImageChange(e) {
  const rawFile = e.target?.files?.[0]
  if (e.target) e.target.value = ''
  if (!rawFile) return
  try {
    const file = await prepareImageFile(rawFile)
    const url = URL.createObjectURL(file)
    if (cropImageUrl.value) URL.revokeObjectURL(cropImageUrl.value)
    cropImageUrl.value = url
    cropFileName.value = file.name ?? 'item.jpg'
  } catch (err) {
    toastStore.error(normalizeApiError(err).message)
  }
}

function onQuickAddCropConfirm(croppedFile) {
  if (!croppedFile) {
    clearCropState()
    return
  }
  const urlToRevoke = cropImageUrl.value
  cropImageUrl.value = ''
  cropFileName.value = ''
  if (urlToRevoke) URL.revokeObjectURL(urlToRevoke)

  if (itemImagePreviewUrl.value) URL.revokeObjectURL(itemImagePreviewUrl.value)
  itemImageFile.value = croppedFile
  itemImagePreviewUrl.value = URL.createObjectURL(croppedFile)
}

function onQuickAddCropCancel() {
  clearCropState()
}

function clearItemImage() {
  clearCropState()
  if (itemImagePreviewUrl.value) URL.revokeObjectURL(itemImagePreviewUrl.value)
  itemImagePreviewUrl.value = null
  itemImageFile.value = null
}

function assignRowDisplayName(row) {
  if (!row) return '—'
  const loc = defaultLocale.value
  const tr = row.translations?.[loc] ?? row.translations?.en ?? {}
  return (tr?.name ?? '').trim() || '—'
}

function getRestaurantItemInTargetCategory(catalogRow, restaurantList, catUuid) {
  if (!catUuid) return null
  const src = catalogRow._sourceCatalogUuid ?? catalogRow.uuid
  const variant = catalogRow._sourceVariantUuid ?? null
  return restaurantList.find(
    i =>
      (i.source_menu_item_uuid ?? null) === src &&
      (i.source_variant_uuid ?? null) === variant &&
      (i.category_uuid ?? null) === catUuid,
  ) ?? null
}

function getRestaurantParentInTargetCategory(sourceCatalogUuid, restaurantList, catUuid) {
  if (!catUuid || !sourceCatalogUuid) return null
  return restaurantList.find(
    i =>
      (i.source_menu_item_uuid ?? null) === sourceCatalogUuid &&
      (i.source_variant_uuid ?? null) == null &&
      (i.category_uuid ?? null) === catUuid,
  ) ?? null
}

function isAssignRowPlacedInTargetCategory(row, restaurantList, catUuid) {
  if (row._isWithVariantsParent) {
    return !!getRestaurantParentInTargetCategory(row._sourceCatalogUuid, restaurantList, catUuid)
  }
  if (row._sourceVariantUuid != null) {
    return !!(
      getRestaurantItemInTargetCategory(row, restaurantList, catUuid) ||
      getRestaurantParentInTargetCategory(row._sourceCatalogUuid, restaurantList, catUuid)
    )
  }
  return !!getRestaurantItemInTargetCategory(row, restaurantList, catUuid)
}

function getRestaurantItemForCatalogRow(catalogRow, restaurantList) {
  const src = catalogRow._sourceCatalogUuid ?? catalogRow.uuid
  const variant = catalogRow._sourceVariantUuid ?? null
  return restaurantList.find(
    i =>
      (i.source_menu_item_uuid ?? null) === src &&
      (i.source_variant_uuid ?? null) === variant,
  ) ?? null
}

const assignFilteredRows = computed(() => {
  const q = (assignSearchQuery.value ?? '').trim().toLowerCase()
  const list = assignCatalogRows.value
  if (!q) return list
  return list.filter(row => assignRowDisplayName(row).toLowerCase().includes(q))
})

async function fetchAssignCatalogList() {
  const rid = props.restaurant?.uuid
  const catUuid = form.value.categoryUuid
  if (!rid || !catUuid) return
  assignCatalogLoading.value = true
  assignCatalogError.value = ''
  selectedAssignRow.value = null
  try {
    const [restaurantRes, userRes] = await Promise.all([
      restaurantService.listMenuItems(rid),
      menuItemService.list(),
    ])
    const restaurantList = Array.isArray(restaurantRes?.data) ? restaurantRes.data : []
    const userList = Array.isArray(userRes?.data) ? userRes.data : []
    restaurantMenuItemsForAssign.value = restaurantList.filter(i =>
      ALLOWED_ASSIGN_CATALOG_TYPES.includes(i.type ?? 'simple'),
    )
    const loc = defaultLocale.value
    const catalogItems = []
    for (const raw of userList) {
      if ((raw.restaurant_uuid ?? null) !== null) continue
      if (!ALLOWED_ASSIGN_CATALOG_TYPES.includes(raw.type ?? 'simple')) continue
      const catalogItem = MenuItem.fromApi({ data: raw })
      if (catalogItem.type === 'with_variants') {
        const skus = catalogItem.variant_skus ?? []
        if (skus.length === 0) continue
        const baseName = (catalogItem.translations?.[loc]?.name ?? catalogItem.translations?.en?.name ?? '').trim() || '—'
        const groupOrder = catalogItem.variantOptionGroupNames ?? []
        catalogItems.push({
          uuid: `catalog-parent-${catalogItem.uuid}`,
          _isWithVariantsParent: true,
          _sourceCatalogUuid: catalogItem.uuid,
          _addSource: 'catalog',
          translations: { [loc]: { name: baseName } },
          price: skus[0]?.price != null ? Number(skus[0].price) : null,
        })
        for (const sku of skus) {
          const skuUuid = sku.uuid ?? sku?.uuid
          if (!skuUuid) continue
          const variantLabel = typeof sku.displayLabel === 'function'
            ? sku.displayLabel(groupOrder)
            : (Object.values(sku.option_values || {}).filter(Boolean).join(', ') || '—')
          catalogItems.push({
            uuid: `catalog-${catalogItem.uuid}-${skuUuid}`,
            _sourceCatalogUuid: catalogItem.uuid,
            _sourceVariantUuid: skuUuid,
            _addSource: 'catalog',
            translations: { [loc]: { name: `${baseName} – ${variantLabel}` } },
            price: sku.price != null ? Number(sku.price) : null,
          })
        }
      } else {
        const json = catalogItem.toJSON()
        json._addSource = 'catalog'
        catalogItems.push(json)
      }
    }
    assignCatalogRows.value = catalogItems.filter(
      row => !isAssignRowPlacedInTargetCategory(row, restaurantMenuItemsForAssign.value, catUuid),
    )
  } catch (e) {
    assignCatalogError.value = normalizeApiError(e).message
    assignCatalogRows.value = []
    restaurantMenuItemsForAssign.value = []
  } finally {
    assignCatalogLoading.value = false
  }
}

async function saveAssignItem() {
  const rid = props.restaurant?.uuid
  const catUuid = form.value.categoryUuid
  const row = selectedAssignRow.value
  if (!rid || !catUuid || !row || saving.value) return
  saving.value = true
  saveError.value = ''
  fieldErrors.value = {}
  imageUploadWarn.value = ''
  try {
    const restaurantList = restaurantMenuItemsForAssign.value
    const inCat = restaurantList.filter(i => (i.category_uuid ?? null) === catUuid)
    const nextSortOrder = inCat.length
    const existing = getRestaurantItemForCatalogRow(row, restaurantList)
    let restaurantItemUuid = null

    if (existing) {
      await restaurantService.updateMenuItem(rid, existing.uuid, {
        category_uuid: catUuid,
        sort_order: nextSortOrder,
      })
      restaurantItemUuid = existing.uuid
    } else if (row._isWithVariantsParent) {
      const res = await restaurantService.createMenuItem(rid, {
        source_menu_item_uuid: row._sourceCatalogUuid,
        category_uuid: catUuid,
        sort_order: nextSortOrder,
      })
      restaurantItemUuid = res?.data?.uuid ?? null
    } else {
      const payload = {
        source_menu_item_uuid: row._sourceCatalogUuid ?? row.uuid,
        category_uuid: catUuid,
        sort_order: nextSortOrder,
      }
      if (row._sourceVariantUuid) payload.source_variant_uuid = row._sourceVariantUuid
      const res = await restaurantService.createMenuItem(rid, payload)
      restaurantItemUuid = res?.data?.uuid ?? null
    }

    if (restaurantItemUuid && form.value.isActive === false) {
      await restaurantService.updateMenuItem(rid, restaurantItemUuid, { is_active: false })
    }

    lastSavedName.value = assignRowDisplayName(row)
    lastSavedCategory.value = categories.value.find(c => c.uuid === catUuid)?.name ?? '—'

    emit('item-added', { assigned: true, restaurant_item_uuid: restaurantItemUuid })

    if (form.value.addAnother) {
      onAddAnother()
    } else {
      isDone.value = true
    }
  } catch (err) {
    const normalized = normalizeApiError(err)
    if (normalized.status === 422 && err?.response?.data?.errors) {
      fieldErrors.value = Object.fromEntries(
        Object.entries(err.response.data.errors).map(([k, v]) => [k, Array.isArray(v) ? v[0] : v]),
      )
      saveError.value = normalized.message || t('app.validationError')
    } else {
      saveError.value = normalized.message || t('app.genericError')
    }
  } finally {
    saving.value = false
  }
}

// ── Validation ────────────────────────────────────────────────────────────────
const canGoNext = computed(() => {
  if (step.value === 0) return true
  if (step.value === 1) {
    if (categoryCommitting.value) return false
    if (form.value.intent === 'assign') {
      const hasExisting = !!form.value.categoryUuid
      const hasNewName = !!(form.value.newCategoryPending && String(form.value.newCategoryPending).trim())
      return hasExisting || hasNewName
    }
    return !!form.value.categoryUuid || !!form.value.newCategoryPending
  }
  if (step.value === 2) {
    if (form.value.intent === 'assign') {
      return !!selectedAssignRow.value
    }
    if (!form.value.name.trim()) return false
    if (form.value.type === 'simple' || form.value.type === 'with_variants') {
      if (!modifierGroupsStepValid()) return false
    }
    if (form.value.type === 'with_variants') {
      const groups = form.value.variant_option_groups.filter(
        g => (g.name ?? '').trim() && (g.values ?? []).length > 0,
      )
      if (!groups.length) return false
      if (!form.value.variant_skus.length) return false
      return !form.value.variant_skus.some(
        s => s.price === '' || s.price == null || Number.isNaN(Number(s.price)) || Number(s.price) < 0,
      )
    }
    if (form.value.type === 'combo') {
      return form.value.comboEntries.length > 0
    }
    return true
  }
  return true
})

// ── Derived combo picker (exclude already-added rows by pickerKey) ───────────
const availableComboPickerRows = computed(() => {
  const used = new Set(form.value.comboEntries.map(e => e.pickerKey))
  return comboPickerRows.value.filter(row => !used.has(row.pickerKey))
})

/** Label for combo picker: "Burger — Large" from option_values */
function formatComboSkuDisplayName(baseName, optionValues) {
  if (!optionValues || typeof optionValues !== 'object') return baseName
  const parts = Object.values(optionValues).filter(Boolean)
  return parts.length ? `${baseName} — ${parts.join(', ')}` : baseName
}

function buildComboPickerRowsFromApiItems(rawItems, currentLocale, defaultLocale) {
  const rows = []
  for (const item of rawItems) {
    const trans = item.translations ?? {}
    const baseName = trans[currentLocale]?.name || trans[defaultLocale]?.name || Object.values(trans)[0]?.name || '—'
    const skus = Array.isArray(item.variant_skus) ? item.variant_skus : []
    if (item.type === 'with_variants' && skus.length > 0) {
      for (const sku of skus) {
        rows.push({
          pickerKey: `${item.uuid}::${sku.uuid}`,
          menuItemUuid: item.uuid,
          variantUuid: sku.uuid,
          name: formatComboSkuDisplayName(baseName, sku.option_values),
          price: sku.price != null ? Number(sku.price) : null,
        })
      }
    } else {
      rows.push({
        pickerKey: `${item.uuid}::`,
        menuItemUuid: item.uuid,
        variantUuid: null,
        name: baseName,
        price: item.price != null ? Number(item.price) : null,
      })
    }
  }
  return rows
}

// ── Price formatting ──────────────────────────────────────────────────────────
function formatPrice(amount) {
  const currency = props.restaurant?.currency || 'USD'
  try {
    return new Intl.NumberFormat(i18nLocale.value, {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
    }).format(amount)
  } catch {
    return String(amount)
  }
}

// ── Load data ─────────────────────────────────────────────────────────────────
/** Count restaurant menu rows per category_uuid (all item types). */
function countMenuItemsByCategoryUuid(rawItems) {
  const map = new Map()
  if (!Array.isArray(rawItems)) return map
  for (const item of rawItems) {
    const cid = item?.category_uuid
    if (cid == null || cid === '') continue
    map.set(cid, (map.get(cid) ?? 0) + 1)
  }
  return map
}

/**
 * When assigning from catalog with a pending new category name, create the category on the server
 * before opening the catalog picker (assign flow needs a real category UUID).
 */
async function createPendingCategoryIfNeeded() {
  const pending = (form.value.newCategoryPending ?? '').trim()
  if (!pending || form.value.categoryUuid) return true
  const rid = props.restaurant?.uuid
  const menuUuid = primaryMenuUuid.value
  if (!rid || !menuUuid) {
    categoryStepError.value = t('app.quickAddNoMenuForCategory')
    return false
  }
  categoryCommitting.value = true
  categoryStepError.value = ''
  try {
    const defaultLoc = props.restaurant?.default_locale ?? 'en'
    const res = await restaurantService.createCategory(rid, menuUuid, {
      translations: { [defaultLoc]: { name: pending } },
      sort_order: categories.value.length,
    })
    const catUuid = res?.data?.uuid
    if (!catUuid) {
      categoryStepError.value = t('app.genericError')
      return false
    }
    categories.value.push({ uuid: catUuid, name: pending, itemCount: 0 })
    form.value.categoryUuid = catUuid
    form.value.newCategoryPending = ''
    form.value.newCategoryName = ''
    return true
  } catch (e) {
    categoryStepError.value = normalizeApiError(e).message || t('app.genericError')
    return false
  } finally {
    categoryCommitting.value = false
  }
}

async function loadCategories() {
  if (categoriesLoaded.value || categoriesLoading.value) return
  const uuid = props.restaurant?.uuid
  if (!uuid) return
  categoriesLoading.value = true
  try {
    const menus = await restaurantService.listMenus(uuid)
    const menuList = menus?.data ?? []
    if (!menuList.length) {
      primaryMenuUuid.value = null
      categoriesLoaded.value = true
      return
    }
    const menuUuid = menuList[0].uuid
    primaryMenuUuid.value = menuUuid
    const [catSettled, itemsSettled] = await Promise.allSettled([
      restaurantService.listCategories(uuid, menuUuid),
      restaurantService.listMenuItems(uuid),
    ])
    const rawCats = catSettled.status === 'fulfilled' ? (catSettled.value?.data ?? []) : []
    const rawItems = itemsSettled.status === 'fulfilled' ? (itemsSettled.value?.data ?? []) : []
    const countByCat = countMenuItemsByCategoryUuid(rawItems)
    const defaultLocale = props.restaurant?.default_locale ?? 'en'
    const currentLocale = i18nLocale.value
    categories.value = rawCats.map(cat => {
      const trans = cat.translations ?? {}
      const name = trans[currentLocale]?.name || trans[defaultLocale]?.name || Object.values(trans)[0]?.name || '—'
      return { uuid: cat.uuid, name, itemCount: countByCat.get(cat.uuid) ?? 0 }
    })
  } catch {
    // Non-critical; wizard still works
  } finally {
    categoriesLoading.value = false
    categoriesLoaded.value = true
  }
}

async function loadRestaurantItems() {
  if (itemsLoaded.value || itemsLoading.value) return
  const uuid = props.restaurant?.uuid
  if (!uuid) return
  itemsLoading.value = true
  try {
    const resp = await restaurantService.listMenuItems(uuid)
    const raw = resp?.data ?? []
    const defaultLocale = props.restaurant?.default_locale ?? 'en'
    const currentLocale = i18nLocale.value
    const eligible = raw.filter(item => item.type !== 'combo')
    comboPickerRows.value = buildComboPickerRowsFromApiItems(eligible, currentLocale, defaultLocale)
  } catch {
    // Non-critical
  } finally {
    itemsLoading.value = false
    itemsLoaded.value = true
  }
}

// ── Open/close ────────────────────────────────────────────────────────────────
function openSheet() {
  clearItemImage()
  imageUploadWarn.value = ''
  assignSearchQuery.value = ''
  assignCatalogError.value = ''
  assignCatalogRows.value = []
  restaurantMenuItemsForAssign.value = []
  selectedAssignRow.value = null
  assignCatalogLoading.value = false
  categoryStepError.value = ''
  categoryCommitting.value = false
  form.value = freshForm()
  fieldErrors.value = {}
  saveError.value = ''
  step.value = 0
  isDone.value = false
  sheetOpen.value = true
  categoriesLoaded.value = false
  itemsLoaded.value = false
  // Pre-load categories (fresh counts each open)
  nextTick(() => {
    loadCategories()
    updateCartesianSkus()
  })
}

function closeSheet() {
  clearCropState()
  sheetOpen.value = false
  setTimeout(() => {
    step.value = 0
    isDone.value = false
  }, 400)
}

// ── Drag to dismiss (handle only) ─────────────────────────────────────────────
const dragStartY = ref(0)
const dragDelta = ref(0)
const dragging = ref(false)

/** Pull down this far on the handle to dismiss (not confused with a short tap). */
const DISMISS_DRAG_PX = 90
/** Movement below this after release = treat as tap on handle (same as legacy click-to-close). */
const HANDLE_TAP_MAX_PX = 12

function setSheetDragOffset(px) {
  const el = sheetRootRef.value
  if (el) el.style.setProperty('--qa-drag-y', `${Math.round(px)}px`)
}

function clearSheetDragOffset() {
  const el = sheetRootRef.value
  if (el) el.style.removeProperty('--qa-drag-y')
}

function onHandleTouchStart(e) {
  if (saving.value) return
  dragStartY.value = e.touches[0].clientY
  dragging.value = true
  dragDelta.value = 0
}
function onHandleTouchMove(e) {
  if (!dragging.value || saving.value) return
  dragDelta.value = Math.max(0, e.touches[0].clientY - dragStartY.value)
  setSheetDragOffset(dragDelta.value)
}
function onHandleTouchEnd() {
  const delta = dragDelta.value
  dragging.value = false
  clearSheetDragOffset()
  if (!saving.value) {
    if (delta > DISMISS_DRAG_PX) closeSheet()
    else if (delta < HANDLE_TAP_MAX_PX) closeSheet()
  }
  dragDelta.value = 0
}
function onHandleTouchCancel() {
  dragging.value = false
  clearSheetDragOffset()
  dragDelta.value = 0
}

// ── Step navigation ───────────────────────────────────────────────────────────
function goBack() {
  if (step.value > 0) {
    step.value--
    nextTick(() => bodyRef.value?.scrollTo(0, 0))
  }
}

async function goNext() {
  if (!canGoNext.value) return
  fieldErrors.value = {}

  if (step.value === 1 && form.value.intent === 'assign') {
    const ok = await createPendingCategoryIfNeeded()
    if (!ok) return
  }

  if (step.value < TOTAL_STEPS - 1) {
    step.value++
    if (step.value === 2 && form.value.intent === 'assign') {
      await fetchAssignCatalogList()
    }
    if (step.value === 2 && form.value.intent === 'create' && form.value.type === 'combo') {
      loadRestaurantItems()
    }
    nextTick(() => bodyRef.value?.scrollTo(0, 0))
  } else {
    await save()
  }
}

// ── Category helpers ──────────────────────────────────────────────────────────
function selectCategory(uuid) {
  form.value.categoryUuid = uuid
  form.value.newCategoryPending = ''
  form.value.newCategoryName = ''
}

function onNewCatNameInput() {
  // Clear pending when user is typing fresh
  if (form.value.newCategoryPending && form.value.newCategoryName !== form.value.newCategoryPending) {
    form.value.newCategoryPending = ''
  }
}

function confirmNewCategory() {
  const name = form.value.newCategoryName.trim()
  if (!name) return
  form.value.newCategoryPending = name
  form.value.categoryUuid = null
}

function onCreateCategoryEnter() {
  confirmNewCategory()
}

// ── Variant helpers (catalog-style option groups + Cartesian SKUs) ────────────
function stableOptionValuesKey(ov) {
  if (!ov || typeof ov !== 'object') return '{}'
  const keys = Object.keys(ov).sort()
  const o = {}
  for (const k of keys) o[k] = ov[k]
  return JSON.stringify(o)
}

function cartesianProduct(groups) {
  if (!groups.length) return []
  const [first, ...rest] = groups
  const firstCombos = (first.values ?? []).map(v => ({ [first.name]: v }))
  if (!rest.length) return firstCombos
  const restCombos = cartesianProduct(rest)
  return firstCombos.flatMap(opt => restCombos.map(r => ({ ...opt, ...r })))
}

function normalizedVariantGroupsFromForm() {
  return form.value.variant_option_groups
    .filter(g => (g.name ?? '').trim() && (g.values ?? []).length > 0)
    .map(g => ({ name: g.name.trim(), values: g.values }))
}

function updateCartesianSkus() {
  const groups = normalizedVariantGroupsFromForm()
  const combos = cartesianProduct(groups)
  const existingByKey = {}
  for (const row of form.value.variant_skus) {
    existingByKey[stableOptionValuesKey(row.option_values)] = row.price
  }
  form.value.variant_skus = combos.map(opt => ({
    option_values: opt,
    label: Object.values(opt).filter(Boolean).join(', '),
    price: existingByKey[stableOptionValuesKey(opt)] ?? '',
  }))
}

function syncGroupValues(gIdx) {
  const grp = form.value.variant_option_groups[gIdx]
  if (!grp) return
  const text = (grp.valuesText ?? '').trim()
  grp.values = text ? text.split(/[\n,]+/).map(v => v.trim()).filter(Boolean) : []
  updateCartesianSkus()
}

function addOptionGroup() {
  form.value.variant_option_groups.push({ name: '', values: [], valuesText: '' })
}

function removeOptionGroup(idx) {
  form.value.variant_option_groups.splice(idx, 1)
  updateCartesianSkus()
}

watch(() => form.value.type, (v) => {
  if (v === 'with_variants') nextTick(() => updateCartesianSkus())
  if (v === 'combo') {
    form.value.modifier_groups = []
    clearItemImage()
  }
})

watch(() => form.value.intent, () => {
  selectedAssignRow.value = null
  assignCatalogRows.value = []
  assignSearchQuery.value = ''
  categoryStepError.value = ''
})

watch(
  defaultLocale,
  (loc) => {
    const l = loc || 'en'
    for (const grp of form.value.modifier_groups ?? []) {
      for (const mod of grp.options ?? []) {
        if (!mod.translations) mod.translations = {}
        if (!mod.translations[l]) mod.translations[l] = { name: '' }
      }
    }
  },
  { immediate: true },
)

onBeforeUnmount(() => clearItemImage())

// ── Combo helpers ─────────────────────────────────────────────────────────────
function addComboEntry() {
  const key = comboPickerKey.value
  if (!key) return
  const row = comboPickerRows.value.find(r => r.pickerKey === key)
  if (!row) return
  form.value.comboEntries.push({
    pickerKey: row.pickerKey,
    uuid: row.menuItemUuid,
    variant_uuid: row.variantUuid,
    name: row.name,
    price: row.price,
    quantity: 1,
  })
  comboPickerKey.value = ''
}
function removeComboEntry(idx) {
  form.value.comboEntries.splice(idx, 1)
}
function changeQty(idx, delta) {
  const entry = form.value.comboEntries[idx]
  const next = Math.max(1, entry.quantity + delta)
  form.value.comboEntries[idx] = { ...entry, quantity: next }
}

// ── Save ──────────────────────────────────────────────────────────────────────
async function save() {
  const uuid = props.restaurant?.uuid
  if (!uuid || saving.value) return
  if (form.value.intent === 'assign') {
    await saveAssignItem()
    return
  }
  saving.value = true
  saveError.value = ''
  imageUploadWarn.value = ''
  fieldErrors.value = {}

  const payload = {
    type: form.value.type,
    name: form.value.name.trim(),
    description: form.value.description.trim() || null,
    is_active: form.value.isActive,
  }

  if (form.value.categoryUuid) {
    payload.category_uuid = form.value.categoryUuid
  } else if (form.value.newCategoryPending) {
    payload.new_category_name = form.value.newCategoryPending
  }

  if (form.value.type === 'simple') {
    payload.price = form.value.price !== '' ? parseFloat(form.value.price) : null
  }

  if (form.value.type === 'combo') {
    payload.combo_price = form.value.comboPrice !== '' ? parseFloat(form.value.comboPrice) : null
    payload.combo_entries = form.value.comboEntries.map(e => ({
      menu_item_uuid: e.uuid,
      quantity: e.quantity,
      ...(e.variant_uuid ? { variant_uuid: e.variant_uuid } : {}),
    }))
  }

  if (form.value.type === 'with_variants') {
    payload.variant_option_groups = form.value.variant_option_groups
      .filter(g => (g.name ?? '').trim() && (g.values ?? []).length > 0)
      .map(g => ({ name: g.name.trim(), values: g.values }))
    payload.variant_skus = form.value.variant_skus.map(s => ({
      option_values: s.option_values,
      price: Number(s.price),
    }))
  }

  if (form.value.type === 'simple' || form.value.type === 'with_variants') {
    const mg = buildQuickAddModifierGroupsPayload()
    if (mg.length) payload.modifier_groups = mg
  }

  const fileToUpload = itemImageFile.value

  try {
    const result = await restaurantService.quickAddMenuItem(uuid, payload)
    const catalogUuid = result?.data?.uuid

    if (catalogUuid && fileToUpload) {
      try {
        let file = await prepareImageFile(fileToUpload)
        file = await compressImageToMaxBytes(file, MAX_IMAGE_BYTES)
        await menuItemService.uploadImage(catalogUuid, file)
      } catch {
        const msg = t('app.quickAddPhotoUploadFailed')
        if (form.value.addAnother) {
          toastStore.show({ message: msg, type: 'info', duration: 6500 })
        } else {
          imageUploadWarn.value = msg
        }
      }
    }
    clearItemImage()

    lastSavedName.value = form.value.name
    lastSavedCategory.value = result.category?.name
      ?? (form.value.newCategoryPending || (form.value.categoryUuid
        ? (categories.value.find(c => c.uuid === form.value.categoryUuid)?.name ?? '')
        : ''))

    // If new category was created, add it to local list (first item just added)
    if (result.category) {
      categories.value.unshift({ uuid: result.category.uuid, name: result.category.name, itemCount: 1 })
    } else {
      const cat = categories.value.find(c => c.uuid === form.value.categoryUuid)
      if (cat) cat.itemCount = (cat.itemCount ?? 0) + 1
    }

    emit('item-added', result)

    if (form.value.addAnother) {
      onAddAnother()
    } else {
      isDone.value = true
    }
  } catch (err) {
    const normalized = normalizeApiError(err)
    if (normalized.status === 422 && err?.response?.data?.errors) {
      fieldErrors.value = Object.fromEntries(
        Object.entries(err.response.data.errors).map(([k, v]) => [k, Array.isArray(v) ? v[0] : v])
      )
      saveError.value = normalized.message || t('app.validationError')
    } else {
      saveError.value = normalized.message || t('app.genericError')
    }
  } finally {
    saving.value = false
  }
}

function onAddAnother() {
  clearItemImage()
  imageUploadWarn.value = ''
  assignSearchQuery.value = ''
  assignCatalogError.value = ''
  assignCatalogRows.value = []
  restaurantMenuItemsForAssign.value = []
  selectedAssignRow.value = null
  assignCatalogLoading.value = false
  categoryStepError.value = ''
  categoryCommitting.value = false
  const keepCatUuid = form.value.categoryUuid
  const keepCatPending = form.value.newCategoryPending
  const keepIntent = form.value.intent
  form.value = freshForm()
  form.value.intent = keepIntent
  form.value.categoryUuid = keepCatUuid
  form.value.newCategoryPending =
    keepIntent === 'assign' ? false : keepCatPending
  fieldErrors.value = {}
  saveError.value = ''
  step.value = 0
  isDone.value = false
  nextTick(() => bodyRef.value?.scrollTo(0, 0))
}

// Reset cached data when restaurant changes
watch(() => props.restaurant?.uuid, () => {
  categories.value = []
  categoriesLoaded.value = false
  comboPickerRows.value = []
  itemsLoaded.value = false
})

defineExpose({ open: openSheet })
</script>

<style scoped>
/*
 * Design tokens — QRestoo brand
 * primary : #e85a2b  (orange)
 * text    : #333333  (charcoal)
 * bg      : #f8f6f6  (background-light) / #fff (surface)
 * muted   : #64748b
 * font    : 'Plus Jakarta Sans', system-ui, sans-serif  (inherited from body)
 */

/* ── FAB ───────────────────────────────────────────────────────────────────── */
.quick-add-fab {
  position: fixed;
  bottom: 12rem;
  right: 1rem;
  z-index: 9998;
  min-height: 48px;
  padding: 0 1rem;
  border-radius: 9999px;
  border: none;
  /* --quick-add-fab-accent set inline from restaurant primary_color */
  background: var(--quick-add-fab-accent, #e85a2b);
  color: #fff;
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  cursor: pointer;
  box-shadow: 0 4px 20px color-mix(in srgb, var(--quick-add-fab-accent, #e85a2b) 42%, transparent);
  transition: transform 0.15s, opacity 0.15s;
  font-family: inherit;
}
.quick-add-fab:hover { transform: scale(1.03); }
.quick-add-fab:focus-visible {
  outline: 2px solid #fff;
  outline-offset: 2px;
}
.quick-add-fab__icon { font-size: 1.125rem; }
.quick-add-fab__label { font-size: 0.875rem; font-weight: 700; }

@media (max-width: 420px) {
  .quick-add-fab { bottom: 15rem; right: 0.75rem; }
}
@media (max-width: 315px) {
  .quick-add-fab { bottom: 17rem; }
}
@media (min-width: 421px) and (max-width: 767px) {
  .quick-add-fab { bottom: 12rem; right: 1rem; }
}
@media (min-width: 768px) {
  .quick-add-fab { bottom: 9.5rem; right: 1rem; }
}

/* ── Overlay ───────────────────────────────────────────────────────────────── */
.quick-add-overlay {
  position: fixed;
  inset: 0;
  z-index: 10001;
  background: rgba(0, 0, 0, 0.45);
  cursor: default;
}

/* ── Sheet ─────────────────────────────────────────────────────────────────── */
.quick-add-sheet {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 10002;
  background: #fff;
  border-radius: 1.25rem 1.25rem 0 0;
  display: flex;
  flex-direction: column;
  max-height: 93vh;
  box-shadow: 0 -4px 32px rgba(0, 0, 0, 0.12);
  will-change: transform;
  font-family: 'Plus Jakarta Sans', system-ui, sans-serif;
  /* Drag offset set from JS (--qa-drag-y) so desktop translateX(-50%) is preserved */
  --qa-drag-y: 0px;
  transform: translateY(var(--qa-drag-y));
  /* Reduces double-tap zoom delay on mobile; form fields use ≥16px to avoid iOS focus zoom */
  touch-action: manipulation;
}

@media (min-width: 640px) {
  .quick-add-sheet {
    left: 50%;
    right: auto;
    bottom: 1rem;
    transform: translateX(-50%) translateY(var(--qa-drag-y));
    width: 100%;
    max-width: 480px;
    border-radius: 1.25rem;
    box-shadow: 0 8px 48px rgba(0, 0, 0, 0.18);
  }
}

/* ── Handle ────────────────────────────────────────────────────────────────── */
.quick-add-sheet__handle-area {
  padding: 12px 0 4px;
  display: flex;
  justify-content: center;
  flex-shrink: 0;
  cursor: grab;
}
.quick-add-sheet__handle {
  width: 36px;
  height: 4px;
  border-radius: 2px;
  background: #e2e8f0;
}

/* ── Business context strip ───────────────────────────────────────────────── */
.quick-add-sheet__biz-strip {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 20px 10px;
  padding: 10px 12px;
  border-radius: 10px;
  background: #fff7f3;
  border: 1px solid #fbd0c0;
  color: #7c2d12;
  font-size: 13px;
  font-weight: 600;
  line-height: 1.35;
}
.quick-add-sheet__biz-strip-icon {
  font-size: 20px;
  color: #e85a2b;
  flex-shrink: 0;
}
.quick-add-sheet__biz-strip-text { flex: 1; min-width: 0; }

/* ── Header ────────────────────────────────────────────────────────────────── */
.quick-add-sheet__head {
  padding: 4px 20px 10px;
  flex-shrink: 0;
}
.quick-add-sheet__eyebrow {
  font-size: 11px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #e85a2b;
  font-weight: 700;
  margin-bottom: 3px;
}
.quick-add-sheet__title {
  font-size: 1.5rem;
  font-weight: 700;
  color: #333333;
  line-height: 1.2;
  margin: 0;
}
.quick-add-sheet__title :deep(em) {
  font-style: italic;
  color: #e85a2b;
}

/* ── Progress pips ─────────────────────────────────────────────────────────── */
.quick-add-sheet__progress {
  display: flex;
  gap: 5px;
  padding: 0 20px 12px;
  flex-shrink: 0;
}
.quick-add-sheet__pip {
  height: 3px;
  border-radius: 2px;
  background: #e2e8f0;
  transition: background 0.3s, width 0.3s;
  width: 20px;
}
.quick-add-sheet__pip--done { background: #fbd0c0; }
.quick-add-sheet__pip--active { background: #e85a2b; width: 36px; }

/* ── Body ──────────────────────────────────────────────────────────────────── */
.quick-add-sheet__body {
  flex: 1;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding: 0 20px 16px;
  min-height: 0;
  touch-action: pan-y;
  overscroll-behavior-y: contain;
}
.quick-add-sheet__body::-webkit-scrollbar { width: 3px; }
.quick-add-sheet__body::-webkit-scrollbar-thumb { background: #e2e8f0; border-radius: 2px; }

/* ── Footer ────────────────────────────────────────────────────────────────── */
.quick-add-sheet__foot {
  padding: 12px 20px;
  padding-bottom: max(12px, env(safe-area-inset-bottom));
  border-top: 1px solid #f1f5f9;
  display: flex;
  gap: 10px;
  flex-shrink: 0;
  background: #fff;
}

.quick-add-sheet__foot-right {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: stretch;
  gap: 10px;
}

.quick-add-sheet__btn-foot-cancel {
  min-height: 48px;
  padding: 0 16px;
  border-radius: 10px;
  border: 1.5px solid #e2e8f0;
  background: #fff;
  color: #64748b;
  font-size: 15px;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;
  transition: background 0.15s;
}
.quick-add-sheet__btn-foot-cancel:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}
.quick-add-sheet__btn-foot-cancel:not(:disabled):hover {
  background: #f8fafc;
}
.quick-add-sheet__btn-foot-cancel:not(:disabled):active {
  background: #f1f5f9;
}

/* ── Buttons ───────────────────────────────────────────────────────────────── */
.quick-add-sheet__btn-back {
  min-height: 48px;
  padding: 0 18px;
  border-radius: 10px;
  border: 1.5px solid #e2e8f0;
  background: #f8f6f6;
  color: #64748b;
  font-size: 15px;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.15s;
}
.quick-add-sheet__btn-back:disabled { opacity: 0; pointer-events: none; }
.quick-add-sheet__btn-back:not(:disabled):hover { background: #f1f5f9; }
.quick-add-sheet__btn-back:not(:disabled):active { background: #e2e8f0; }

.quick-add-sheet__btn-next {
  flex: 1;
  min-height: 48px;
  border-radius: 10px;
  border: none;
  background: #e85a2b;
  color: #fff;
  font-size: 15px;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  box-shadow: 0 2px 12px rgba(232, 90, 43, 0.3);
  transition: opacity 0.15s, transform 0.1s, box-shadow 0.15s;
}
.quick-add-sheet__btn-next:disabled { opacity: 0.4; cursor: not-allowed; box-shadow: none; }
.quick-add-sheet__btn-next:not(:disabled):hover { opacity: 0.92; }
.quick-add-sheet__btn-next:not(:disabled):active { opacity: 0.85; transform: scale(0.98); box-shadow: none; }

.quick-add-sheet__btn-primary {
  min-height: 48px;
  width: 100%;
  border-radius: 10px;
  border: none;
  background: #e85a2b;
  color: #fff;
  font-size: 15px;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
  box-shadow: 0 2px 12px rgba(232, 90, 43, 0.3);
  transition: opacity 0.15s;
}
.quick-add-sheet__btn-primary:active { opacity: 0.85; }
.quick-add-sheet__btn-secondary {
  min-height: 48px;
  width: 100%;
  border-radius: 10px;
  border: 1.5px solid #e2e8f0;
  background: #f8f6f6;
  color: #64748b;
  font-size: 15px;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  transition: background 0.15s;
}
.quick-add-sheet__btn-secondary:hover { background: #f1f5f9; }
.quick-add-sheet__btn-secondary:active { background: #e2e8f0; }

/* ── Section label ─────────────────────────────────────────────────────────── */
.quick-add-sheet__sec-label {
  font-size: 11px;
  letter-spacing: 0.09em;
  text-transform: uppercase;
  color: #64748b;
  font-weight: 700;
  display: block;
  margin-bottom: 10px;
}
.quick-add-sheet__sec-label--after-intent {
  margin-top: 22px;
}

/* ── Step 0: intent (radio cards) ──────────────────────────────────────────── */
.quick-add-sheet__intent-fieldset {
  border: none;
  margin: 0;
  padding: 0;
  min-width: 0;
}
.quick-add-sheet__intent-legend {
  padding: 0;
  margin-bottom: 10px;
}
.quick-add-sheet__intent-cards {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.quick-add-sheet__intent-card {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  width: 100%;
  min-height: 52px;
  cursor: pointer;
  text-align: left;
  font-family: inherit;
  border-radius: 14px;
  border: 2px solid #e2e8f0;
  padding: 14px 14px 14px 12px;
  background: #fff;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.05);
  transition: border-color 0.18s ease, background 0.18s ease, box-shadow 0.18s ease;
}
.quick-add-sheet__intent-card:hover {
  border-color: #fbd0c0;
  background: #fffdfb;
  box-shadow: 0 2px 10px rgba(232, 90, 43, 0.08);
}
.quick-add-sheet__intent-card:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(232, 90, 43, 0.35);
}
.quick-add-sheet__intent-card--sel {
  border-color: #e85a2b;
  background: linear-gradient(135deg, #fff8f5 0%, #fff 100%);
  box-shadow: 0 2px 14px rgba(232, 90, 43, 0.14);
}
.quick-add-sheet__intent-card-radio {
  flex-shrink: 0;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  border: 2px solid #cbd5e1;
  margin-top: 2px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: border-color 0.15s, background 0.15s;
}
.quick-add-sheet__intent-card--sel .quick-add-sheet__intent-card-radio {
  border-color: #e85a2b;
  background: #fff;
}
.quick-add-sheet__intent-card-radio-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: transparent;
  transition: background 0.15s;
}
.quick-add-sheet__intent-card--sel .quick-add-sheet__intent-card-radio-dot {
  background: #e85a2b;
}
.quick-add-sheet__intent-card-main {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.quick-add-sheet__intent-card-title {
  font-size: 15px;
  font-weight: 700;
  color: #0f172a;
  line-height: 1.25;
}
.quick-add-sheet__intent-card-desc {
  font-size: 13px;
  color: #475569;
  line-height: 1.45;
}
.quick-add-sheet__intent-card-icon {
  flex-shrink: 0;
  font-size: 26px;
  color: #e85a2b;
  opacity: 0.85;
  margin-top: 2px;
}
.quick-add-sheet__intent-card--sel .quick-add-sheet__intent-card-icon {
  opacity: 1;
}
@media (min-width: 480px) {
  .quick-add-sheet__intent-cards {
    flex-direction: row;
    align-items: stretch;
  }
  .quick-add-sheet__intent-card {
    flex: 1;
    min-width: 0;
    position: relative;
    flex-direction: column;
    align-items: flex-start;
    padding: 16px 44px 16px 14px;
  }
  .quick-add-sheet__intent-card-icon {
    position: absolute;
    top: 14px;
    right: 12px;
    margin-top: 0;
  }
}
.quick-add-sheet__type-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
  margin-bottom: 14px;
}
.quick-add-sheet__type-chip {
  border: 1.5px solid #e2e8f0;
  border-radius: 12px;
  padding: 14px 6px 12px;
  cursor: pointer;
  background: #f8f6f6;
  text-align: center;
  user-select: none;
  transition: border-color 0.15s, background 0.15s;
  min-height: 48px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 3px;
}
.quick-add-sheet__type-chip:hover { border-color: #fbd0c0; background: #fff; }
.quick-add-sheet__type-chip:active { background: #fff3ee; }
.quick-add-sheet__type-chip--sel {
  border: 2px solid #e85a2b;
  background: #fff3ee;
}
.quick-add-sheet__chip-icon {
  font-size: 18px;
  color: #e85a2b;
}
.quick-add-sheet__chip-label {
  font-size: 12px;
  font-weight: 700;
  color: #333333;
}
.quick-add-sheet__chip-sub {
  font-size: 10px;
  color: #64748b;
  line-height: 1.3;
}
.quick-add-sheet__type-hint {
  background: #fff3ee;
  border: 1px solid #fbd0c0;
  border-radius: 10px;
  padding: 11px 14px;
  font-size: 13px;
  color: #9a3412;
  line-height: 1.55;
}

.quick-add-sheet__assign-cat-hint {
  font-size: 13px;
  color: #475569;
  line-height: 1.5;
  margin: 0 0 14px;
  padding: 12px 14px;
  background: #f8fafc;
  border-radius: 10px;
  border: 1px solid #e2e8f0;
}

/* ── Category cards ────────────────────────────────────────────────────────── */
.quick-add-sheet__cat-list { margin-bottom: 2px; }
.quick-add-sheet__cat-card {
  border: 1.5px solid #e2e8f0;
  border-radius: 12px;
  padding: 14px 16px;
  cursor: pointer;
  background: #f8f6f6;
  display: flex;
  align-items: center;
  gap: 12px;
  transition: border-color 0.15s, background 0.15s;
  margin-bottom: 8px;
  min-height: 48px;
  user-select: none;
  width: 100%;
  text-align: left;
  animation: qai-slide-in 0.2s ease;
}
.quick-add-sheet__cat-card:hover { border-color: #fbd0c0; background: #fff; }
.quick-add-sheet__cat-card:active { background: #fff3ee; }
.quick-add-sheet__cat-card--sel {
  border: 2px solid #e85a2b;
  background: #fff3ee;
}
.quick-add-sheet__cat-info { flex: 1; min-width: 0; }
.quick-add-sheet__cat-name {
  font-size: 15px;
  font-weight: 600;
  color: #333333;
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.quick-add-sheet__cat-meta { font-size: 12px; color: #64748b; margin-top: 1px; }
.quick-add-sheet__cat-check {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  border: 1.5px solid #e2e8f0;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s;
  color: #fff;
}
.quick-add-sheet__cat-check--on {
  background: #e85a2b;
  border-color: #e85a2b;
}

/* ── Assign catalog list (step 2, intent assign) ───────────────────────────── */
.quick-add-assign-list {
  list-style: none;
  margin: 14px 0 0;
  padding: 8px;
  max-height: min(52vh, 360px);
  overflow-y: auto;
  overscroll-behavior: contain;
  border: 1.5px solid #e2e8f0;
  border-radius: 14px;
  background: #f8fafc;
  display: flex;
  flex-direction: column;
  gap: 6px;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.6);
}
.quick-add-assign-list::-webkit-scrollbar {
  width: 6px;
}
.quick-add-assign-list::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 3px;
}
.quick-add-assign-li {
  list-style: none;
  margin: 0;
  padding: 0;
}
.quick-add-assign-row {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-height: 48px;
  padding: 11px 12px;
  border-radius: 11px;
  border: 2px solid #e8ecf0;
  background: #fff;
  cursor: pointer;
  font-family: inherit;
  font-size: inherit;
  text-align: left;
  color: inherit;
  transition: border-color 0.18s ease, background 0.18s ease, box-shadow 0.18s ease;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
}
.quick-add-assign-row:hover {
  border-color: #fbd0c0;
  background: #fffdfb;
  box-shadow: 0 2px 8px rgba(232, 90, 43, 0.08);
}
.quick-add-assign-row:focus {
  outline: none;
}
.quick-add-assign-row:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(232, 90, 43, 0.35);
  border-color: #e85a2b;
}
.quick-add-assign-row:active {
  background: #fff8f5;
}
.quick-add-assign-row--sel {
  border-color: #e85a2b;
  background: linear-gradient(135deg, #fff8f5 0%, #fff 55%);
  box-shadow: 0 2px 12px rgba(232, 90, 43, 0.12);
}
.quick-add-assign-row__name {
  flex: 1;
  min-width: 0;
  font-size: 14px;
  font-weight: 600;
  color: #0f172a;
  line-height: 1.35;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-align: left;
}
.quick-add-assign-row__end {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
}
.quick-add-assign-row__price {
  font-size: 13px;
  font-weight: 700;
  color: #c2410c;
  font-variant-numeric: tabular-nums;
  letter-spacing: -0.01em;
  white-space: nowrap;
}
.quick-add-assign-row--sel .quick-add-assign-row__price {
  color: #9a3412;
}
.quick-add-assign-row__check {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  border: 1.5px solid #e2e8f0;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fff;
  transition: border-color 0.15s, background 0.15s;
}
.quick-add-assign-row__check--on {
  background: #e85a2b;
  border-color: #e85a2b;
  color: #fff;
}
.quick-add-assign-row__check-icon {
  font-size: 14px !important;
  line-height: 1 !important;
}
@media (prefers-reduced-motion: reduce) {
  .quick-add-assign-row {
    transition: none;
  }
}

/* ── Empty cat ─────────────────────────────────────────────────────────────── */
.quick-add-sheet__empty-cat {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 24px 0 16px;
  text-align: center;
  gap: 6px;
}
.quick-add-sheet__empty-cat-icon {
  font-size: 30px;
  color: #e85a2b;
  opacity: 0.4;
  margin-bottom: 2px;
}
.quick-add-sheet__empty-cat-title { font-size: 15px; font-weight: 700; color: #333333; }
.quick-add-sheet__empty-cat-sub { font-size: 13px; color: #64748b; line-height: 1.5; max-width: 240px; }

/* ── OR divider ────────────────────────────────────────────────────────────── */
.quick-add-sheet__or-divider {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 12px 0;
}
.quick-add-sheet__or-line { flex: 1; height: 1px; background: #e2e8f0; }
.quick-add-sheet__or-text { font-size: 12px; color: #94a3b8; white-space: nowrap; }

/* ── Create box ────────────────────────────────────────────────────────────── */
.quick-add-sheet__create-box {
  border: 1.5px dashed #fbd0c0;
  border-radius: 12px;
  padding: 14px 16px;
  background: #fff3ee;
  margin-bottom: 4px;
}
.quick-add-sheet__create-label { font-size: 12px; color: #64748b; font-weight: 600; margin-bottom: 8px; }
.quick-add-sheet__create-row { display: flex; gap: 8px; align-items: stretch; }
.quick-add-sheet__create-input {
  flex: 1;
  min-height: 48px;
  padding: 11px 12px;
  font-size: 16px;
  border-radius: 10px;
  border: 1.5px solid #e2e8f0;
  background: #fff;
  color: #333333;
  font-family: inherit;
  outline: none;
  transition: border-color 0.2s;
}
.quick-add-sheet__create-input:focus { border-color: #e85a2b; }
.quick-add-sheet__create-btn {
  min-height: 48px;
  padding: 0 16px;
  background: #e85a2b;
  color: #fff;
  border: none;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;
  transition: opacity 0.15s;
}
.quick-add-sheet__create-btn:disabled { opacity: 0.35; cursor: not-allowed; }
.quick-add-sheet__create-btn:not(:disabled):hover { opacity: 0.9; }
.quick-add-sheet__create-btn:not(:disabled):active { opacity: 0.8; }

.quick-add-sheet__cat-pending {
  margin-top: 8px;
  font-size: 12px;
  font-weight: 600;
  color: #16a34a;
  display: flex;
  align-items: center;
  gap: 4px;
}

/* ── Form fields ───────────────────────────────────────────────────────────── */
.quick-add-sheet__field { margin-bottom: 16px; }
.quick-add-sheet__two-col { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-bottom: 4px; }
.quick-add-sheet__label {
  font-size: 12px;
  color: #64748b;
  font-weight: 600;
  margin-bottom: 6px;
  display: block;
}
.quick-add-sheet__input {
  width: 100%;
  min-height: 48px;
  padding: 12px 14px;
  font-size: 16px;
  border-radius: 10px;
  border: 1.5px solid #e2e8f0;
  background: #f8f6f6;
  color: #333333;
  font-family: inherit;
  outline: none;
  transition: border-color 0.2s, background 0.2s;
  appearance: none;
  -webkit-appearance: none;
}
.quick-add-sheet__input:focus { border-color: #e85a2b; background: #fff; }
.quick-add-sheet__input--error { border-color: #dc2626; }
.quick-add-sheet__textarea {
  width: 100%;
  min-height: 72px;
  padding: 12px 14px;
  font-size: 16px;
  border-radius: 10px;
  border: 1.5px solid #e2e8f0;
  background: #f8f6f6;
  color: #333333;
  outline: none;
  resize: vertical;
  font-family: inherit;
  transition: border-color 0.2s, background 0.2s;
}
.quick-add-sheet__textarea:focus { border-color: #e85a2b; background: #fff; }

.quick-add-sheet__field-error { font-size: 12px; color: #dc2626; margin-top: 4px; font-weight: 500; }

.quick-add-sheet__sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

/* ── Quick-add photo + modifiers ─────────────────────────────────────────── */
.quick-add-sheet__photo-section {
  margin: 18px 0 16px;
  padding-top: 4px;
  border-top: 1px solid #f1f5f9;
}
.quick-add-sheet__photo-hint {
  font-size: 13px;
  color: #64748b;
  line-height: 1.45;
  margin: -4px 0 12px;
}
.quick-add-sheet__photo-row {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  flex-wrap: wrap;
}
.quick-add-sheet__photo-preview-wrap {
  flex-shrink: 0;
  width: 88px;
  height: 88px;
  border-radius: 12px;
  overflow: hidden;
  border: 1.5px solid #e2e8f0;
  background: #f8f6f6;
}
.quick-add-sheet__photo-preview {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.quick-add-sheet__photo-placeholder {
  flex-shrink: 0;
  width: 88px;
  height: 88px;
  border-radius: 12px;
  border: 1.5px dashed #e2e8f0;
  background: #fafafa;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #94a3b8;
}
.quick-add-sheet__photo-placeholder-icon { font-size: 32px; }
.quick-add-sheet__photo-actions {
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: flex-start;
  min-width: 0;
}
.quick-add-sheet__photo-upload-btn {
  min-height: 44px;
  padding: 0 16px;
  border-radius: 10px;
  border: 1.5px solid #e85a2b;
  background: #fff;
  color: #e85a2b;
  font-size: 14px;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
}
.quick-add-sheet__photo-upload-btn:hover { background: #fff3ee; }
.quick-add-sheet__photo-upload-btn:focus-visible {
  outline: 2px solid #e85a2b;
  outline-offset: 2px;
}
.quick-add-sheet__photo-remove-btn {
  min-height: 40px;
  padding: 0 12px;
  border: none;
  background: transparent;
  color: #64748b;
  font-size: 13px;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 3px;
}
.quick-add-sheet__photo-remove-btn:focus-visible {
  outline: 2px solid #e85a2b;
  outline-offset: 2px;
  border-radius: 4px;
}

.quick-add-sheet__mod-section {
  margin-bottom: 16px;
  padding-bottom: 4px;
  border-bottom: 1px solid #f1f5f9;
}
.quick-add-sheet__mod-head {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 4px;
}
.quick-add-sheet__mod-hint { margin-bottom: 12px !important; }
.quick-add-sheet__mod-group {
  background: #f8f6f6;
  border: 1.5px solid #e2e8f0;
  border-radius: 12px;
  padding: 12px;
  margin-bottom: 12px;
}
.quick-add-sheet__mod-group-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}
.quick-add-sheet__mod-group-num {
  font-size: 11px;
  font-weight: 800;
  color: #94a3b8;
  letter-spacing: 0.06em;
}
.quick-add-sheet__mod-radio-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px 16px;
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid #e2e8f0;
}
.quick-add-sheet__mod-radio-label {
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
  width: 100%;
}
.quick-add-sheet__mod-radio {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 44px;
  cursor: pointer;
  font-size: 14px;
  color: #333333;
}
.quick-add-sheet__mod-radio input {
  width: 1.125rem;
  height: 1.125rem;
  accent-color: #e85a2b;
}
.quick-add-sheet__add-opt-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  min-height: 40px;
  padding: 0 10px;
  margin-bottom: 10px;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  background: #fff;
  font-size: 12px;
  font-weight: 700;
  font-family: inherit;
  color: #64748b;
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s;
}
.quick-add-sheet__add-opt-chip:hover { border-color: #fbd0c0; background: #fff3ee; }
.quick-add-sheet__add-opt-chip:focus-visible {
  outline: 2px solid #e85a2b;
  outline-offset: 2px;
}
.quick-add-sheet__mod-option {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 10px;
  margin-bottom: 8px;
}
.quick-add-sheet__mod-option-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}
.quick-add-sheet__mod-option-num {
  font-size: 10px;
  font-weight: 800;
  color: #94a3b8;
  letter-spacing: 0.05em;
}

/* ── Variants ──────────────────────────────────────────────────────────────── */
.quick-add-sheet__variant-row {
  background: #f8f6f6;
  border: 1.5px solid #e2e8f0;
  border-radius: 10px;
  padding: 12px;
  margin-bottom: 8px;
  display: grid;
  grid-template-columns: 1fr 90px 36px;
  gap: 8px;
  align-items: end;
  animation: qai-slide-in 0.2s ease;
}
.quick-add-sheet__variant-row .quick-add-sheet__input { background: #fff; min-height: 44px; }

.quick-add-sheet__opt-groups-head {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 4px;
}
.quick-add-sheet__add-group-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  min-height: 40px;
  padding: 0 12px;
  border-radius: 999px;
  border: 1.5px solid #fbd0c0;
  background: #fff;
  font-size: 13px;
  font-weight: 700;
  font-family: inherit;
  color: #e85a2b;
  cursor: pointer;
}
.quick-add-sheet__add-group-chip:active { background: #fff3ee; }
.quick-add-sheet__add-group-chip:focus-visible {
  outline: 2px solid #e85a2b;
  outline-offset: 2px;
}
.quick-add-sheet__opt-group {
  background: #f8f6f6;
  border: 1.5px solid #e2e8f0;
  border-radius: 10px;
  padding: 12px;
  margin-bottom: 10px;
}
.quick-add-sheet__opt-group-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}
.quick-add-sheet__opt-group-num {
  font-size: 11px;
  font-weight: 800;
  color: #94a3b8;
  letter-spacing: 0.06em;
}
.quick-add-sheet__opt-group .quick-add-sheet__textarea { min-height: 64px; font-size: 16px; }

/* iOS Safari zooms focused inputs when font-size < 16px — enforce minimum on all controls in this sheet */
.quick-add-sheet input:not([type="checkbox"]):not([type="radio"]):not([type="range"]),
.quick-add-sheet textarea,
.quick-add-sheet select {
  font-size: max(16px, 1rem);
}
.quick-add-sheet__variant-price-row {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 8px;
  padding: 10px 12px;
  background: #f8f6f6;
  border: 1.5px solid #e2e8f0;
  border-radius: 10px;
}
.quick-add-sheet__variant-price-label {
  flex: 1;
  min-width: 0;
  font-size: 14px;
  font-weight: 600;
  color: #333333;
  line-height: 1.3;
}
.quick-add-sheet__variant-price-input {
  width: 100px;
  flex-shrink: 0;
  min-height: 44px;
  background: #fff;
}

.quick-add-sheet__rm-btn {
  background: none;
  border: 1.5px solid #e2e8f0;
  border-radius: 8px;
  width: 36px;
  height: 44px;
  cursor: pointer;
  color: #94a3b8;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: color 0.15s, border-color 0.15s;
}
.quick-add-sheet__rm-btn:hover { color: #dc2626; border-color: #fca5a5; }
.quick-add-sheet__rm-btn:active { color: #dc2626; border-color: #dc2626; }

.quick-add-sheet__add-btn {
  width: 100%;
  min-height: 44px;
  background: none;
  border: 1.5px dashed #fbd0c0;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  font-family: inherit;
  color: #e85a2b;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  transition: border-color 0.15s, background 0.15s;
  margin-top: 4px;
}
.quick-add-sheet__add-btn:hover { background: #fff3ee; border-color: #e85a2b; }
.quick-add-sheet__add-btn:active { background: #ffe4d6; }

/* ── Combo ─────────────────────────────────────────────────────────────────── */
.quick-add-sheet__combo-row {
  background: #f8f6f6;
  border: 1.5px solid #e2e8f0;
  border-radius: 10px;
  padding: 12px 14px;
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 8px;
  animation: qai-slide-in 0.2s ease;
}
.quick-add-sheet__combo-name { flex: 1; font-size: 14px; font-weight: 500; color: #333333; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.quick-add-sheet__qty-wrap { display: flex; align-items: center; gap: 8px; flex-shrink: 0; }
.quick-add-sheet__qty-btn {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: 1.5px solid #e2e8f0;
  background: #fff;
  cursor: pointer;
  font-size: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #64748b;
  flex-shrink: 0;
  transition: border-color 0.15s, color 0.15s;
}
.quick-add-sheet__qty-btn:hover { border-color: #e85a2b; color: #e85a2b; }
.quick-add-sheet__qty-btn:active { border-color: #e85a2b; color: #e85a2b; }
.quick-add-sheet__qty-val { font-size: 15px; font-weight: 700; color: #333333; min-width: 20px; text-align: center; }
.quick-add-sheet__combo-add-row { display: flex; gap: 8px; align-items: stretch; margin-top: 10px; }
.quick-add-sheet__select {
  flex: 1;
  min-height: 48px;
  padding: 0 14px;
  font-size: 16px;
  font-family: inherit;
  border-radius: 10px;
  border: 1.5px solid #e2e8f0;
  background: #f8f6f6;
  color: #333333;
  outline: none;
  cursor: pointer;
  appearance: none;
  -webkit-appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg width='12' height='7' viewBox='0 0 12 7' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1l5 5 5-5' stroke='%2364748b' stroke-width='1.4' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 14px center;
  padding-right: 36px;
  transition: border-color 0.2s;
}
.quick-add-sheet__select:focus { border-color: #e85a2b; outline: none; }
.quick-add-sheet__empty-hint {
  font-size: 13px;
  color: #64748b;
  text-align: center;
  padding: 16px 0;
}

/* ── Review ────────────────────────────────────────────────────────────────── */
.quick-add-sheet__review-card {
  background: #f8f6f6;
  border: 1.5px solid #e2e8f0;
  border-radius: 12px;
  overflow: hidden;
  margin-bottom: 10px;
}
.quick-add-sheet__review-head {
  font-size: 10px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  font-weight: 700;
  color: #e85a2b;
  padding: 9px 14px;
  background: #fff;
  border-bottom: 1px solid #f1f5f9;
  margin: 0;
}
.quick-add-sheet__review-row {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  padding: 10px 14px;
  border-bottom: 1px solid #f1f5f9;
  gap: 12px;
}
.quick-add-sheet__review-row:last-child { border-bottom: none; }
.quick-add-sheet__rk { font-size: 13px; color: #64748b; flex-shrink: 0; }
.quick-add-sheet__rv { font-size: 13px; font-weight: 600; color: #333333; text-align: right; word-break: break-word; }

/* ── Toggles ───────────────────────────────────────────────────────────────── */
.quick-add-sheet__toggles { padding: 4px 0 8px; }
.quick-add-sheet__toggle-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 13px 0;
  border-bottom: 1px solid #f1f5f9;
  min-height: 48px;
}
.quick-add-sheet__toggle-row:last-child { border-bottom: none; }
.quick-add-sheet__toggle-title { font-size: 15px; font-weight: 600; color: #333333; }
.quick-add-sheet__toggle-sub { font-size: 12px; color: #64748b; margin-top: 2px; }
.quick-add-sheet__tog {
  width: 44px;
  height: 26px;
  border-radius: 13px;
  background: #e2e8f0;
  position: relative;
  cursor: pointer;
  transition: background 0.2s;
  flex-shrink: 0;
  border: none;
  padding: 0;
}
.quick-add-sheet__tog--on { background: #e85a2b; }
.quick-add-sheet__tog-knob {
  position: absolute;
  top: 3px;
  left: 3px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #fff;
  transition: transform 0.2s;
  box-shadow: 0 1px 4px rgba(0,0,0,0.15);
  display: block;
}
.quick-add-sheet__tog--on .quick-add-sheet__tog-knob { transform: translateX(18px); }

/* ── Skeleton ──────────────────────────────────────────────────────────────── */
.quick-add-sheet__skeleton-list { padding: 4px 0; }
.quick-add-sheet__skeleton-row {
  height: 64px;
  border-radius: 12px;
  background: linear-gradient(90deg, #f8f6f6 25%, #f1ede9 50%, #f8f6f6 75%);
  background-size: 200% 100%;
  animation: qai-shimmer 1.2s ease-in-out infinite;
  margin-bottom: 8px;
}

/* ── Success ───────────────────────────────────────────────────────────────── */
.quick-add-sheet__success {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 32px 20px 24px;
  gap: 10px;
}
.quick-add-sheet__check-ring {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: #fff3ee;
  border: 2px solid #e85a2b;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #e85a2b;
  margin-bottom: 4px;
}
.quick-add-sheet__success-title {
  font-size: 1.75rem;
  font-weight: 700;
  color: #333333;
  margin: 0;
}
.quick-add-sheet__success-sub {
  font-size: 14px;
  color: #64748b;
  line-height: 1.6;
  max-width: 260px;
  margin: 0;
}
.quick-add-sheet__success-warn {
  font-size: 13px;
  color: #b45309;
  line-height: 1.5;
  max-width: 280px;
  margin: 0;
  padding: 10px 12px;
  border-radius: 10px;
  background: #fffbeb;
  border: 1px solid #fde68a;
}
.quick-add-sheet__success-actions {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  margin-top: 6px;
}

/* ── Transitions ───────────────────────────────────────────────────────────── */
.quick-add-fab-enter-active,
.quick-add-fab-leave-active {
  transition: opacity 0.2s, transform 0.2s;
}
.quick-add-fab-enter-from,
.quick-add-fab-leave-to {
  opacity: 0;
  transform: scale(0.9);
}

.quick-add-overlay-enter-active,
.quick-add-overlay-leave-active {
  transition: opacity 0.25s;
}
.quick-add-overlay-enter-from,
.quick-add-overlay-leave-to {
  opacity: 0;
}

.quick-add-sheet-enter-active {
  transition: transform 0.35s cubic-bezier(0.22, 1, 0.36, 1), opacity 0.2s;
}
.quick-add-sheet-leave-active {
  transition: transform 0.3s cubic-bezier(0.4, 0, 1, 1), opacity 0.25s;
}
.quick-add-sheet-enter-from,
.quick-add-sheet-leave-to {
  transform: translateY(100%);
  opacity: 0;
}

@media (min-width: 640px) {
  .quick-add-sheet-enter-from,
  .quick-add-sheet-leave-to {
    transform: translateX(-50%) scale(0.95) translateY(12px);
  }
}

/* ── Animations ────────────────────────────────────────────────────────────── */
@keyframes qai-slide-in {
  from { opacity: 0; transform: translateY(6px); }
  to { opacity: 1; transform: translateY(0); }
}
@keyframes qai-shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}
.step-anim {
  animation: qai-slide-in 0.22s ease;
}
@keyframes spin {
  to { transform: rotate(360deg); }
}
</style>
